// src/pages/field/JobDetail.tsx
//
// Workhorse full-page view for a single field_job. Inline-editable fields
// organized into five collapsible cards (Customer / Schedule / Address /
// Assignment / Notes) backed by the shared InlineField primitive. Status,
// service line, priority shown as badges in the header card; status
// transitions still flow through the action buttons in the top bar.
// Line items + totals owned by JobLineItemsEditor. Lock state on both the
// editor and the inline fields kicks in once job.invoice_id is set.
// ============================================================================

import React, { useCallback, useEffect, useRef, useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'
import { useAuth } from '@/lib/auth'
import { snapshotInvoiceCost } from '@/lib/invoiceCostSnapshot'
import { useIsMobile } from '@/lib/useIsMobile'
import {
  ArrowLeft, Navigation, CheckCircle2, FileText, ExternalLink, ChevronDown,
  AlertTriangle, X, Repeat, Clock, XCircle, Send, Ban, Pause, Play, Plus,
} from 'lucide-react'
import { format, getDate, parseISO } from 'date-fns'
import { toast } from 'sonner'
import { createVendorAssignment } from '@/lib/createVendorAssignment'
import { detectConflicts, type ConflictingJob } from '@/lib/scheduleConflicts'
import type { Database } from '@/lib/database.types'
import {
  buildStatusTransitionPayload,
  NEXT_STATUS,
  HOLD_STATUSES,
  canEnterHold,
  resumeTarget,
  type FieldJobStatus,
} from '@/pages/field/jobStatusTransition'
import { JOB_STATUS_CONFIG } from '@/pages/field/FieldHub'
import { JobPhotoGrid } from '@/components/JobPhotoStrip'
import { JobPriorityChip } from '@/components/JobPriorityIndicator'
import JobLineItemsEditor from '@/components/JobLineItemsEditor'
import { notifyPortal } from '@/lib/portalNotify'
import {
  projectFieldJobLineItemsToInvoiceJsonb,
  fetchContactEmail,
  type FieldJobLineItem,
} from '@/lib/jobInvoiceConvert'
import InlineField, { type InlineFieldOption } from '@/components/InlineField'

const PRIORITY_OPTIONS: InlineFieldOption[] = [
  { value: 'low', label: 'Low' },
  { value: 'normal', label: 'Normal' },
  { value: 'high', label: 'High' },
  { value: 'urgent', label: 'Urgent' },
]

// Conflict warning banner styles. Module-scope so the rgba literals don't
// trip the hex hook on the JSX consumer site. Amber 251,191,36 matches the
// rest of JobDetail's non-themed dark palette.
const conflictBannerStyles: Record<string, React.CSSProperties> = {
  wrap:      { padding: '12px 14px', background: 'var(--warning-dim)', border: '1px solid var(--warning-border-strong)', borderRadius: 14, display: 'flex', gap: 10 },
  icon:      { width: 16, height: 16, color: 'var(--warning)', flexShrink: 0, marginTop: 1 },
  title:     { margin: 0, fontSize: 12, fontWeight: 700, color: 'var(--warning)', flex: 1 },
  subtitle:  { margin: '0 0 8px', fontSize: 11, color: 'var(--ink-secondary)' },
  dismiss:   { background: 'none', border: 'none', cursor: 'pointer', padding: 2, color: 'var(--ink-tertiary)', display: 'flex', borderRadius: 4 },
  row:       { display: 'flex', alignItems: 'center', gap: 8, padding: '6px 8px', borderRadius: 6, fontSize: 12, color: 'var(--ink-primary)', background: 'var(--hover-bg)', border: 'none', textAlign: 'left', cursor: 'pointer', width: '100%' },
  jobNumber: { fontFamily: 'monospace', color: 'var(--ink-secondary)' },
  jobTitle:  { flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  jobTime:   { color: 'var(--ink-secondary)', whiteSpace: 'nowrap' },
}

// Recurring-series context banner styles. Purple to distinguish from the
// amber conflict and lock banners — this is informational ("you are looking
// at one instance of a series"), not a warning. Same wrap shape so the
// banners visually rhyme.
const seriesBannerStyles: Record<string, React.CSSProperties> = {
  wrap:     { padding: '12px 14px', background: 'var(--accent-dim)', border: '1px solid var(--accent-border)', borderRadius: 14, display: 'flex', gap: 10 },
  icon:     { width: 16, height: 16, color: 'var(--accent-text)', flexShrink: 0, marginTop: 1 },
  title:    { margin: 0, fontSize: 12, fontWeight: 700, color: 'var(--accent-text)' },
  subtitle: { margin: '4px 0 0', fontSize: 11, color: 'var(--ink-secondary)' },
  actions:  { display: 'flex', gap: 8, marginTop: 10, flexWrap: 'wrap' },
  skipBtn:  { padding: '6px 12px', borderRadius: 8, background: 'var(--field-bg)', border: '1px solid var(--border-default)', color: 'var(--ink-primary)', fontSize: 12, fontWeight: 600, cursor: 'pointer' },
  endBtn:   { padding: '6px 12px', borderRadius: 8, background: 'var(--danger-dim)', border: '1px solid var(--danger-border-strong)', color: 'var(--danger)', fontSize: 12, fontWeight: 600, cursor: 'pointer' },
  endedTag: { display: 'inline-block', marginLeft: 8, fontSize: 10, fontWeight: 700, padding: '1px 7px', borderRadius: 100, background: 'var(--border-default)', color: 'var(--ink-secondary)', textTransform: 'uppercase', letterSpacing: '0.06em' },
}

// End-series confirmation modal. Destructive — red CTA, count-led headline.
// Overlay covers the whole viewport; clicking it cancels.
const endSeriesModalStyles: Record<string, React.CSSProperties> = {
  overlay:    { position: 'fixed', inset: 0, background: 'var(--overlay-scrim)', backdropFilter: 'blur(6px)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 300, padding: 24 },
  content:    { background: 'var(--surface)', border: '1px solid var(--border-default)', borderRadius: 18, padding: 24, width: '100%', maxWidth: 460 },
  headline:   { margin: 0, fontSize: 16, fontWeight: 800, color: 'var(--ink-primary)', lineHeight: 1.35 },
  body:       { margin: '12px 0 20px', fontSize: 13, color: 'var(--ink-secondary)', lineHeight: 1.5 },
  actions:    { display: 'flex', gap: 10, justifyContent: 'flex-end' },
  cancelBtn:  { padding: '9px 16px', borderRadius: 9, background: 'var(--field-bg)', border: '1px solid var(--border-default)', color: 'var(--ink-primary)', fontSize: 13, fontWeight: 600, cursor: 'pointer' },
  confirmBtn: { padding: '9px 16px', borderRadius: 9, background: 'var(--danger-dim)', border: '1px solid var(--danger-border-strong)', color: 'var(--danger)', fontSize: 13, fontWeight: 700, cursor: 'pointer' },
}

// Vendor-assignment card + Send/Cancel modal styles. Module-scope per the
// JobDetail convention so the rgba/hex literals don't trip the no-hex hook
// on the inline-style consumer sites. Palette mirrors the other banners/
// modals in this file (NOT the tokens.css system used by vendor-respond
// pages — JobDetail is intentionally non-themed).
const vendorAssignStyles: Record<string, React.CSSProperties> = {
  cardWrap:        { background: 'var(--hover-bg)', border: '1px solid var(--border-subtle)', borderRadius: 14, overflow: 'hidden' },
  cardHeader:      { width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 18px', background: 'none', border: 'none', cursor: 'pointer', textAlign: 'left' },
  cardLabel:       { fontSize: 12, fontWeight: 700, color: 'var(--ink-secondary)', textTransform: 'uppercase', letterSpacing: '0.08em' },
  cardLabelGroup:  { display: 'flex', alignItems: 'center', gap: 8 },
  pendingTag:      { fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 100, background: 'var(--warning-dim)', color: 'var(--warning)', textTransform: 'uppercase', letterSpacing: '0.06em' },
  chevron:         { width: 14, height: 14, color: 'var(--ink-tertiary)', transition: 'transform 150ms ease' },
  body:            { padding: '4px 18px 16px', display: 'flex', flexDirection: 'column', gap: 12 },
  statusRow:       { display: 'flex', alignItems: 'flex-start', gap: 10 },
  statusIcon:      { width: 16, height: 16, flexShrink: 0, marginTop: 2 },
  iconPending:     { color: 'var(--warning)' },
  iconAccepted:    { color: 'var(--positive)' },
  iconDeclined:    { color: 'var(--ink-tertiary)' },
  iconCancelled:   { color: 'var(--ink-tertiary)' },
  iconNone:        { color: 'var(--ink-tertiary)' },
  statusText:      { margin: 0, fontSize: 13, fontWeight: 600, color: 'var(--ink-primary)', lineHeight: 1.45 },
  statusMeta:      { margin: '3px 0 0', fontSize: 11.5, color: 'var(--ink-secondary)', lineHeight: 1.45 },
  declineReason:   { margin: '6px 0 0', fontSize: 12, color: 'var(--ink-secondary)', fontStyle: 'italic', lineHeight: 1.45 },
  actions:         { display: 'flex', gap: 8, flexWrap: 'wrap' },
  sendBtn:         { display: 'inline-flex', alignItems: 'center', gap: 6, padding: '8px 14px', borderRadius: 8, background: 'var(--accent-dim)', border: '1px solid var(--focus-ring)', color: 'var(--accent-text)', fontSize: 12, fontWeight: 700, cursor: 'pointer' },
  cancelBtn:       { display: 'inline-flex', alignItems: 'center', gap: 6, padding: '8px 14px', borderRadius: 8, background: 'var(--danger-dim)', border: '1px solid var(--danger-border-strong)', color: 'var(--danger)', fontSize: 12, fontWeight: 700, cursor: 'pointer' },
  hint:            { margin: 0, fontSize: 11, color: 'var(--ink-tertiary)', fontStyle: 'italic' },
  hintIcon:        { width: 11, height: 11, color: 'var(--ink-tertiary)', marginRight: 4, verticalAlign: 'text-bottom' },
}

// Send-assignment modal styles. Overlay/content mirror the End-series modal
// (same dark surface, same border-radius, same blur backdrop) so the page's
// modals visually rhyme.
const vendorSendModalStyles: Record<string, React.CSSProperties> = {
  overlay:    { position: 'fixed', inset: 0, background: 'var(--overlay-scrim)', backdropFilter: 'blur(6px)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 300, padding: 24 },
  content:    { background: 'var(--surface)', border: '1px solid var(--border-default)', borderRadius: 18, padding: 24, width: '100%', maxWidth: 460, maxHeight: '90vh', overflow: 'auto', display: 'flex', flexDirection: 'column' },
  headline:   { margin: 0, fontSize: 16, fontWeight: 800, color: 'var(--ink-primary)', lineHeight: 1.35 },
  helper:     { margin: '10px 0 18px', fontSize: 12, color: 'var(--ink-secondary)', lineHeight: 1.5 },
  label:      { fontSize: 11, fontWeight: 700, color: 'var(--ink-secondary)', textTransform: 'uppercase', letterSpacing: '0.06em', display: 'block', marginBottom: 6 },
  select:     { width: '100%', padding: '10px 12px', background: 'var(--field-bg)', border: '1px solid var(--border-default)', borderRadius: 8, color: 'var(--ink-primary)', fontSize: 13, outline: 'none', boxSizing: 'border-box' },
  input:      { width: '100%', padding: '10px 12px', background: 'var(--field-bg)', border: '1px solid var(--border-default)', borderRadius: 8, color: 'var(--ink-primary)', fontSize: 13, outline: 'none', boxSizing: 'border-box' },
  fieldGroup: { display: 'flex', flexDirection: 'column', gap: 14, marginBottom: 18 },
  fieldHint:  { margin: '6px 0 0', fontSize: 11, color: 'var(--ink-tertiary)', lineHeight: 1.4 },
  actions:    { display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 'auto' },
  cancelBtn:  { padding: '9px 16px', borderRadius: 9, background: 'var(--field-bg)', border: '1px solid var(--border-default)', color: 'var(--ink-primary)', fontSize: 13, fontWeight: 600, cursor: 'pointer' },
  sendBtn:    { padding: '9px 16px', borderRadius: 9, background: 'var(--accent-dim)', border: '1px solid var(--focus-ring)', color: 'var(--accent-text)', fontSize: 13, fontWeight: 700, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 6 },
}

type RecurringSeriesRow = Database['public']['Tables']['recurring_job_series']['Row']

const DOW_NAMES_PLURAL = ['Sundays', 'Mondays', 'Tuesdays', 'Wednesdays', 'Thursdays', 'Fridays', 'Saturdays']
const DOW_NAMES_SHORT  = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']

// Human-readable recurrence rule. "Weekly · Tuesdays" / "Every 2 weeks · Mon/Wed/Fri"
// / "Monthly · day 15". Day-of-month for monthly is derived from start_date.
function humanizeRule(series: RecurringSeriesRow): string {
  if (series.frequency === 'monthly') {
    try { return `Monthly · day ${getDate(parseISO(series.start_date))}` }
    catch { return 'Monthly' }
  }
  const freqLabel = series.frequency === 'biweekly' ? 'Every 2 weeks' : 'Weekly'
  const dows = [...(series.days_of_week ?? [])].sort((a, b) => a - b)
  if (dows.length === 0) return freqLabel
  if (dows.length === 1) return `${freqLabel} · ${DOW_NAMES_PLURAL[dows[0]] ?? ''}`
  return `${freqLabel} · ${dows.map(d => DOW_NAMES_SHORT[d] ?? '').join('/')}`
}

export default function JobDetail() {
  const { id } = useParams()
  const navigate = useNavigate()
  const { workspace, profile } = useAuth()
  const isOwnerOrAdmin = !!profile?.is_owner || profile?.role === 'admin'
  const queryClient = useQueryClient()
  const isMobile = useIsMobile()
  const [generatingInvoice, setGeneratingInvoice] = useState(false)
  const [holdMenuOpen, setHoldMenuOpen] = useState(false)

  // Same query shape as the deep-link single-row path inside FieldHub. Service-
  // role read; RLS scopes to the operator's workspace, so wrong-workspace IDs
  // naturally surface as not-found instead of a leak.
  const { data: job, isLoading } = useQuery({
    queryKey: ['field-job', id],
    queryFn: async () => {
      if (!id) return null
      const { data, error } = await supabase
        .from('field_jobs')
        .select('*, location:locations(id, name, company_id, company:companies(id, name)), vendor:vendors(id, name), service_line:service_lines(id, name)')
        .eq('id', id)
        .maybeSingle()
      if (error) throw error
      return data
    },
    enabled: !!id,
  })

  const updateMutation = useMutation({
    mutationFn: async (updates: any) => {
      if (!id) throw new Error('No job id')
      const { error } = await supabase.from('field_jobs').update(updates).eq('id', id)
      if (error) throw error
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['field-job', id] })
      queryClient.invalidateQueries({ queryKey: ['field-jobs-cal'] })
    },
    onError: () => toast.error('Failed to update'),
  })

  // Inline-edit helper: returns an onSave that issues a partial UPDATE and
  // surfaces the error to InlineField so it can revert + exit edit. The
  // promise-rejection contract is what InlineField listens to.
  const savePatch = useCallback(async (patch: Record<string, any>) => {
    // Capture prior state BEFORE the mutation runs so the schedule-gain
    // detection (NULL → date) reads the value the operator just edited
    // away from, not the post-update one.
    const priorScheduledDate = (job as { scheduled_date?: string | null })?.scheduled_date ?? null
    await updateMutation.mutateAsync(patch)
    if (!priorScheduledDate && typeof patch.scheduled_date === 'string' && patch.scheduled_date.length > 0) {
      notifyPortal({ event: 'job_scheduled', job_id: id! })
    }
    if (patch.status === 'complete') {
      notifyPortal({ event: 'job_completed', job_id: id! })
    }
  }, [updateMutation, job, id])

  // Conflict detection — fires whenever the loaded job's assignment or time
  // bounds change (after savePatch invalidates and refetches). Dismissed
  // state clears on any change so dismissing a "10am conflict with Mike" then
  // reassigning to a new conflicting tech re-surfaces the banner.
  const [conflicts, setConflicts] = useState<ConflictingJob[]>([])
  const [conflictsDismissed, setConflictsDismissed] = useState(false)
  // Per-run cancellation token. Same pattern + reasoning as NewJobModal:
  // bumped synchronously at the top of every effect run (before the guard)
  // so the clear-the-assignee path also invalidates a pending in-flight
  // async, regardless of when the React useEffect cleanup phase actually
  // fires relative to the microtask that resolves an inflight Promise.
  const conflictSeq = useRef(0)
  const jobAgentId   = job?.assigned_agent_id ?? null
  const jobVendorId  = job?.vendor_id ?? null
  const jobDate      = job?.scheduled_date ?? null
  const jobStart     = job?.scheduled_start ?? null
  const jobEnd       = job?.scheduled_end ?? null
  useEffect(() => {
    const mySeq = ++conflictSeq.current
    setConflictsDismissed(false)
    if (!workspace?.id || !jobDate || (!jobAgentId && !jobVendorId)) {
      setConflicts([])
      return
    }
    ;(async () => {
      const checks: Promise<ConflictingJob[]>[] = []
      if (jobAgentId) checks.push(detectConflicts({
        workspaceId: workspace.id, assigneeType: 'employee', assigneeId: jobAgentId,
        date: jobDate, start: jobStart, end: jobEnd, excludeJobId: job?.id,
      }))
      if (jobVendorId) checks.push(detectConflicts({
        workspaceId: workspace.id, assigneeType: 'vendor', assigneeId: jobVendorId,
        date: jobDate, start: jobStart, end: jobEnd, excludeJobId: job?.id,
      }))
      const results = await Promise.all(checks)
      if (mySeq !== conflictSeq.current) return
      const merged = new Map<string, ConflictingJob>()
      for (const list of results) for (const c of list) merged.set(c.id, c)
      setConflicts(Array.from(merged.values()))
    })()
  }, [workspace?.id, job?.id, jobAgentId, jobVendorId, jobDate, jobStart, jobEnd])

  // Option queries for the compound selects. All workspace-scoped, all
  // optional sub-selects on a single InlineField — no shared fetch lifecycle
  // needed beyond react-query's cache.
  const { data: contacts = [] } = useQuery({
    queryKey: ['contacts', 'typeahead-job-detail', workspace?.id],
    queryFn: async () => {
      if (!workspace?.id) return []
      const { data } = await supabase.from('contacts')
        .select('id, first_name, last_name, phone, email')
        .eq('workspace_id', workspace.id)
        .order('first_name', { ascending: true })
      return data ?? []
    },
    enabled: !!workspace?.id,
  })

  const { data: agents = [] } = useQuery({
    queryKey: ['agents-for-job-detail', workspace?.id],
    queryFn: async () => {
      if (!workspace?.id) return []
      const { data } = await supabase.from('profiles')
        .select('id, full_name')
        .eq('workspace_id', workspace.id)
        .order('full_name', { ascending: true })
      return data ?? []
    },
    enabled: !!workspace?.id,
  })

  const { data: locations = [] } = useQuery({
    queryKey: ['locations-for-job-detail', workspace?.id],
    queryFn: async () => {
      if (!workspace?.id) return []
      const { data } = await supabase.from('locations')
        .select('id, name, address_line1, address_line2, city, state, zip, country, company:companies(name)')
        .eq('workspace_id', workspace.id)
        .order('name', { ascending: true })
      return data ?? []
    },
    enabled: !!workspace?.id,
  })

  const { data: vendors = [] } = useQuery({
    queryKey: ['vendors-for-job-detail', workspace?.id],
    queryFn: async () => {
      if (!workspace?.id) return []
      const { data } = await supabase.from('vendors')
        .select('id, name, status')
        .eq('workspace_id', workspace.id)
        .order('name', { ascending: true })
      return (data ?? []).filter((v: any) => v.status !== 'inactive')
    },
    enabled: !!workspace?.id,
  })

  // SEPARATE picker query for the Send-assignment modal: ACTIVE vendors only,
  // with email so we can show emailless rows as disabled (don't silently
  // exclude — operator should see the "add email first" path). Independent
  // from the inline-pointer-field's `vendors` query above by design.
  const { data: assignablePickerVendors = [] } = useQuery({
    queryKey: ['vendors-for-assign-picker', workspace?.id],
    queryFn: async () => {
      if (!workspace?.id) return []
      const { data } = await supabase.from('vendors')
        .select('id, name, email')
        .eq('workspace_id', workspace.id)
        .eq('status', 'active')
        .order('name', { ascending: true })
      return data ?? []
    },
    enabled: !!workspace?.id,
  })

  // Latest vendor_assignments row for this job — drives the Vendor-assignment
  // card's status display and decides whether Send or Cancel is offered.
  // RLS scopes to workspace; ordering by created_at desc + limit 1 picks the
  // most recent (pending wins over historical terminal rows naturally —
  // partial-unique blocks >1 pending, so the latest is the live one if any).
  const { data: latestAssignment } = useQuery({
    queryKey: ['vendor-assignment-latest', id],
    queryFn: async () => {
      if (!id) return null
      const { data, error } = await supabase
        .from('vendor_assignments')
        .select('id, status, budget_offered, responded_at, decline_reason, created_at, vendor_id, vendor:vendors(name)')
        .eq('field_job_id', id)
        .order('created_at', { ascending: false })
        .limit(1)
        .maybeSingle()
      if (error) throw error
      return data as {
        id: string
        status: 'pending' | 'accepted' | 'declined' | 'expired' | 'cancelled'
        budget_offered: number | string | null
        responded_at: string | null
        decline_reason: string | null
        created_at: string
        vendor_id: string
        vendor: { name: string } | null
      } | null
    },
    enabled: !!id,
  })

  // Job-level vendor-actual cost for the margin fold-in (slice 2): SUM of ALL
  // vendor_bills for this job (across every assignment — the denormalized
  // field_job_id makes this a single scan). null when zero bills exist
  // (unknown), distinct from $0. vendor_bills is staff-only (no vendor RLS).
  const { data: jobVendorActual = null } = useQuery({
    queryKey: ['job-vendor-actual', id],
    queryFn: async () => {
      if (!id) return null
      const { data, error } = await supabase
        .from('vendor_bills')
        .select('amount')
        .eq('field_job_id', id)
      if (error) throw error
      if (!data || data.length === 0) return null
      return data.reduce((sum, b) => sum + (Number(b.amount) || 0), 0)
    },
    enabled: !!id,
  })

  // Job-level labor cost for the margin fold-in (slice 3): SUM of field_job_labor
  // for this job. null when zero entries (N/A — NOT flagged partial, unlike vendor).
  // field_job_labor is staff-only (no vendor/portal RLS).
  const { data: jobLaborCost = null } = useQuery({
    queryKey: ['job-labor-cost', id],
    queryFn: async () => {
      if (!id) return null
      const { data, error } = await supabase
        .from('field_job_labor')
        .select('amount')
        .eq('field_job_id', id)
      if (error) throw error
      if (!data || data.length === 0) return null
      return data.reduce((sum, l) => sum + (Number(l.amount) || 0), 0)
    },
    enabled: !!id,
  })

  // Latest vendor lifecycle event for the latest assignment — drives
  // the header chip + the "Mark job complete" banner. Only meaningful
  // when the latest assignment is currently accepted (events table
  // RLS already filters non-accepted out anyway, but we gate the query
  // to spare PostgREST a round-trip).
  const { data: latestVendorEvent } = useQuery({
    queryKey: ['vendor-event-latest-for-detail', latestAssignment?.id],
    enabled: !!latestAssignment?.id && latestAssignment?.status === 'accepted',
    queryFn: async () => {
      const { data, error } = await supabase
        .from('vendor_assignment_events')
        .select('event_type, created_at, note')
        .eq('assignment_id', latestAssignment!.id)
        .order('created_at', { ascending: false })
        .limit(1)
        .maybeSingle()
      if (error) throw error
      return data as { event_type: StaffLifecycleEvent; created_at: string; note: string | null } | null
    },
  })
  const [completionBannerDismissed, setCompletionBannerDismissed] = useState(false)

  const { data: serviceLines = [] } = useQuery({
    queryKey: ['service-lines-for-job-detail', workspace?.id],
    queryFn: async () => {
      if (!workspace?.id) return []
      const { data } = await supabase.from('service_lines')
        .select('id, name')
        .eq('workspace_id', workspace.id)
        .order('name', { ascending: true })
      return data ?? []
    },
    enabled: !!workspace?.id,
  })

  // Build the option arrays for InlineField. Compound writes (contact / agent /
  // location) carry extra fields on each option so the parent's onSave can
  // unpack denormalized columns.
  const contactOptions: InlineFieldOption[] = contacts.map((c: any) => ({
    value: c.id,
    label: `${c.first_name || ''} ${c.last_name || ''}`.trim() || c.email || '(no name)',
    phone: c.phone || '',
  }))
  const agentOptions: InlineFieldOption[] = agents.map((a: any) => ({
    value: a.id,
    label: a.full_name || '(no name)',
  }))
  const locationOptions: InlineFieldOption[] = locations.map((l: any) => ({
    value: l.id,
    label: l.company?.name ? `${l.company.name} – ${l.name}` : l.name,
    address_line1: l.address_line1 || '',
    address_line2: l.address_line2 || '',
    city: l.city || '',
    state: l.state || '',
    zip: l.zip || '',
    country: l.country || '',
  }))
  const vendorOptions: InlineFieldOption[] = vendors.map((v: any) => ({
    value: v.id, label: v.name,
  }))
  const serviceLineOptions: InlineFieldOption[] = serviceLines.map((s: any) => ({
    value: s.id, label: s.name,
  }))

  // ─── Recurring-series context (Chunks 6/7/8) ───────────────────────────
  // Parent series row, fetched only when this job is part of a series.
  // Drives the contextual banner + the Skip/End actions. Cached cheaply;
  // invalidated explicitly on end-series.
  const { data: series } = useQuery<RecurringSeriesRow | null>({
    queryKey: ['recurring-series', job?.series_id],
    queryFn: async () => {
      if (!job?.series_id) return null
      const { data, error } = await supabase
        .from('recurring_job_series')
        .select('*')
        .eq('id', job.series_id)
        .maybeSingle()
      if (error) throw error
      return data
    },
    enabled: !!job?.series_id,
  })

  // End-series preview — count + first/last date of the future-scheduled
  // occurrences that would be deleted. Pre-fetched so the confirm modal
  // opens with the count already in the headline. Same three-predicate
  // filter as the DELETE below, by design: the preview promises the
  // user what the delete will actually do.
  const today = format(new Date(), 'yyyy-MM-dd')
  const { data: endPreview } = useQuery({
    queryKey: ['series-end-preview', job?.series_id, today],
    queryFn: async () => {
      const seriesId = job?.series_id
      if (!seriesId) return { count: 0, first: null as string | null, last: null as string | null }
      const { data, error } = await supabase
        .from('field_jobs')
        .select('scheduled_date')
        .eq('series_id', seriesId)
        .eq('status', 'scheduled')
        .gt('scheduled_date', today)
        .order('scheduled_date', { ascending: true })
      if (error) throw error
      const dates = (data ?? []).map(r => r.scheduled_date).filter((d): d is string => !!d)
      return { count: dates.length, first: dates[0] ?? null, last: dates[dates.length - 1] ?? null }
    },
    enabled: !!job?.series_id && series?.status === 'active',
  })

  const [endModalOpen, setEndModalOpen] = useState(false)
  const [ending, setEnding] = useState(false)

  // ─── Vendor-assignment state ───────────────────────────────────────────────
  // Card open state. Collapsed by default; auto-opens on any TRANSITION
  // into a "needs attention" status — pending (awaiting response),
  // declined or expired (operator should reconcile or resend). Auto-open
  // explicitly does NOT fire for accepted or cancelled — those are
  // resolved states the operator can expand manually if they want
  // context. Manual toggles stick: once the user collapses on a sticky
  // state, a re-render at the same status won't re-open because the
  // effect only fires on the TRANSITION (prev != current).
  //
  // This effect subsumes the dispatch's "default open when pending/
  // declined/expired" rule by treating the first arrival of latestAssignment
  // (prev undefined → current pending|declined|expired) as a transition
  // and opening on it. No separate "initial default" useState computation
  // is needed; the effect-on-arrival pattern handles both first-paint
  // and live updates.
  const NEEDS_ATTENTION = new Set(['pending', 'declined', 'expired'])
  const [vendorCardOpen, setVendorCardOpen] = useState(false)
  const prevAssignStatusRef = useRef<string | null | undefined>(undefined)
  useEffect(() => {
    const status = latestAssignment?.status
    if (status && NEEDS_ATTENTION.has(status) && prevAssignStatusRef.current !== status) {
      setVendorCardOpen(true)
    }
    prevAssignStatusRef.current = status
  }, [latestAssignment?.status])

  const [sendModalOpen, setSendModalOpen] = useState(false)
  const [sending, setSending] = useState(false)
  const [sendVendorId, setSendVendorId] = useState<string>('')
  const [sendBudget, setSendBudget] = useState<string>('')

  const [cancelConfirmOpen, setCancelConfirmOpen] = useState(false)
  const [cancelling, setCancelling] = useState(false)

  // When the Send modal opens, default the vendor pick to the job's current
  // vendor pointer if that vendor is in the assignable picker list; else the
  // first eligible vendor (preferring email-bearing entries). The picker
  // ALSO shows emailless vendors as disabled rows so the operator sees the
  // "add email first" path — we just don't default to one of them.
  const openSendModal = () => {
    const eligible = assignablePickerVendors.filter((v: any) => !!v.email)
    let initial = ''
    if (job?.vendor_id && eligible.some((v: any) => v.id === job.vendor_id)) {
      initial = job.vendor_id
    } else if (eligible.length > 0) {
      initial = eligible[0].id
    }
    setSendVendorId(initial)
    setSendBudget('')
    setSendModalOpen(true)
  }

  const handleSendAssignment = async () => {
    if (!job?.id || !sendVendorId || sending) return
    const chosen = assignablePickerVendors.find((v: any) => v.id === sendVendorId)
    if (!chosen) { toast.error('Pick a vendor'); return }
    if (!chosen.email) {
      toast.error('Vendor has no email — add one on the vendor profile first')
      return
    }
    const budgetNum = sendBudget.trim() ? Number(sendBudget) : null
    if (budgetNum !== null && (Number.isNaN(budgetNum) || budgetNum < 0)) {
      toast.error('Budget must be a non-negative number')
      return
    }
    setSending(true)
    const { data, error } = await createVendorAssignment({
      fieldJobId: job.id,
      vendorId: sendVendorId,
      budgetOffered: budgetNum,
    })
    setSending(false)
    if (error) {
      toast.error(error.message)
      return
    }
    if (data?.warning) {
      toast.warning(`Assignment created, but the email didn't send: ${data.warning}`)
    } else {
      toast.success(`Assignment sent to ${chosen.name}`)
    }
    setSendModalOpen(false)
    queryClient.invalidateQueries({ queryKey: ['vendor-assignment-latest', id] })
    queryClient.invalidateQueries({ queryKey: ['field-job', id] })  // field_jobs.vendor_id may have been updated by the function
  }

  const handleCancelAssignment = async () => {
    if (!latestAssignment?.id || cancelling) return
    setCancelling(true)
    const { error } = await supabase.rpc('cancel_vendor_assignment', { assignment_id: latestAssignment.id })
    setCancelling(false)
    if (error) {
      toast.error(`Failed to cancel: ${error.message}`)
      return
    }
    toast.success('Assignment cancelled')
    setCancelConfirmOpen(false)
    queryClient.invalidateQueries({ queryKey: ['vendor-assignment-latest', id] })
  }

  const handleSkipVisit = () => {
    if (!job) return
    if (job.status === 'cancelled' || job.status === 'complete') return
    if (!window.confirm('Skip this visit? The series continues; this single visit will be marked cancelled.')) return
    updateMutation.mutate(buildStatusTransitionPayload(job.status, 'cancelled'))
    toast.success('Visit skipped')
  }

  const handleEndSeries = async () => {
    const seriesId = job?.series_id
    // Belt-and-suspenders guard against null seriesId silently widening the
    // DELETE scope to the whole field_jobs table. The button shouldn't be
    // reachable without a series_id, but the DELETE is too destructive to
    // rely on UI-level gating alone.
    if (!seriesId) {
      toast.error('No series to end')
      return
    }
    setEnding(true)
    const todayStr = format(new Date(), 'yyyy-MM-dd')

    // Step 1 — mark the series ended FIRST. The keeper filters .eq('status',
    // 'active') so this immediately makes the series inert to further
    // materialization on the next keeper run, regardless of whether the
    // DELETE below succeeds.
    const { error: uErr } = await supabase
      .from('recurring_job_series')
      .update({ status: 'ended', end_date: todayStr })
      .eq('id', seriesId)
    if (uErr) {
      setEnding(false)
      toast.error(`Failed to end series: ${uErr.message}`)
      return
    }

    // Step 2 — DELETE future scheduled occurrences. Three explicit chained
    // predicates: series_id (scope guard), status='scheduled' (preserves
    // history of completed/in-progress/cancelled), scheduled_date > today
    // (strict — preserves today's untouched visit). Dropping any one of
    // these is catastrophic; each is its own .eq()/.gt() call to keep them
    // individually visible at the call site.
    const { error: dErr } = await supabase
      .from('field_jobs')
      .delete()
      .eq('series_id', seriesId)
      .eq('status', 'scheduled')
      .gt('scheduled_date', todayStr)
    if (dErr) {
      setEnding(false)
      toast.error(`Series ended, but failed to remove future visits: ${dErr.message}`)
      return
    }

    setEnding(false)
    setEndModalOpen(false)
    const removedCount = endPreview?.count ?? 0
    const wasCurrentJobDeleted = !!(
      job &&
      job.status === 'scheduled' &&
      job.scheduled_date &&
      job.scheduled_date > todayStr
    )

    queryClient.invalidateQueries({ queryKey: ['field-jobs-cal'] })
    queryClient.invalidateQueries({ queryKey: ['recurring-series', seriesId] })
    queryClient.invalidateQueries({ queryKey: ['series-end-preview', seriesId] })
    toast.success(
      removedCount > 0
        ? `Series ended — ${removedCount} upcoming visit${removedCount === 1 ? '' : 's'} removed`
        : 'Series ended',
    )

    if (wasCurrentJobDeleted) {
      navigate('/app/field')
    } else {
      queryClient.invalidateQueries({ queryKey: ['field-job', id] })
    }
  }

  const handleSendEnRoute = () => {
    if (!job) return
    updateMutation.mutate(buildStatusTransitionPayload(job.status, 'en_route'))
    toast.success('Marked en route')
  }

  const handleStatusAdvance = () => {
    if (!job) return
    const next = NEXT_STATUS[job.status]
    if (!next) return
    updateMutation.mutate(buildStatusTransitionPayload(job.status, next))
    toast.success(`Marked ${JOB_STATUS_CONFIG[next]?.label}`)
  }

  const handleEnterHold = (holdStatus: FieldJobStatus) => {
    if (!job) return
    updateMutation.mutate(buildStatusTransitionPayload(job.status, holdStatus))
    setHoldMenuOpen(false)
    toast.success(`Paused — ${JOB_STATUS_CONFIG[holdStatus]?.label}`)
  }

  const handleResume = () => {
    if (!job) return
    const target = resumeTarget()
    updateMutation.mutate(buildStatusTransitionPayload(job.status, target))
    toast.success(`Resumed — ${JOB_STATUS_CONFIG[target]?.label}`)
  }

  // Job→invoice convert. Fetches the job's normalized line items, projects them
  // into invoice JSONB shape via projectFieldJobLineItemsToInvoiceJsonb, and
  // copies the job-level totals (subtotal/tax/discount/currency) onto the new
  // invoice. Shared helper keeps this aligned with JobDrawer.handleGenerateInvoice
  // so the two convert paths can't drift.
  const handleGenerateInvoice = async () => {
    if (!job || !workspace?.id || generatingInvoice) return
    setGeneratingInvoice(true)
    // Parallel: line items + contact email (job rows don't carry the email
    // — fetchContactEmail is the canonical lookup shared with FieldHub +
    // BillingTab). invoice_number assigned by the trigger (Phase A,
    // race-safe), so the invoice-count query is no longer needed.
    const [{ data: lineRows, error: lineErr }, contactEmail] = await Promise.all([
      // Named columns only — deliberately NOT select('*'): unit_cost (staff-only
      // cost) must never be fetched at the conversion site so it cannot reach
      // invoices.line_items. The projection enumerates keys too (belt-and-suspenders).
      supabase.from('field_job_line_items').select('id, name, description, qty, unit, unit_price, total, product_id, sort_index').eq('field_job_id', job.id).order('sort_index', { ascending: true }),
      fetchContactEmail(job.contact_id),
    ])
    if (lineErr) {
      setGeneratingInvoice(false)
      toast.error(`Failed to load line items: ${lineErr.message}`)
      return
    }
    const projectedLineItems = projectFieldJobLineItemsToInvoiceJsonb((lineRows ?? []) as FieldJobLineItem[])

    const { data: invoice, error } = await supabase.from('invoices').insert({
      workspace_id: workspace.id,
      contact_id: job.contact_id,
      contact_name: job.contact_name,
      contact_email: contactEmail,
      company_name: job.company_name || null,
      status: 'draft',
      line_items: projectedLineItems,
      subtotal: job.subtotal ?? 0,
      tax_rate: job.tax_rate ?? 0,
      tax_amount: job.tax_amount ?? 0,
      total: job.total ?? 0,
      amount_due: job.total ?? 0,
      amount_paid: 0,
      discount_type: job.discount_type ?? 'none',
      discount_value: job.discount_value ?? 0,
      discount_amount: job.discount_amount ?? 0,
      currency: job.currency ?? 'USD',
      issue_date: job.scheduled_date || new Date().toISOString().split('T')[0],
      notes: job.job_notes || job.title,
      // Inherit from the source job; frozen at creation per attribution
      // snapshot precedent (see invoices.service_line_id column comment).
      service_line_id: job.service_line_id ?? null,
    }).select().single()
    setGeneratingInvoice(false)
    if (error || !invoice) {
      toast.error(`Error: ${error?.message || 'Failed to create invoice'}`)
      return
    }
    // Guarded link: only attach the invoice if the job isn't already invoiced (race/stale-cache guard).
    const { data: linkedRows, error: linkErr } = await supabase
      .from('field_jobs')
      .update({ invoice_id: invoice.id })
      .eq('id', job.id)
      .is('invoice_id', null)
      .select('id')
    if (linkErr) {
      // Link failed: void the orphaned invoice we just created, surface the error.
      const { error: voidErr } = await supabase.from('invoices').update({ status: 'void' }).eq('id', invoice.id)
      if (voidErr) {
        toast.error(`Failed to link invoice, and a draft invoice (${invoice.invoice_number ?? invoice.id}) could not be auto-voided — please void it manually.`)
      } else {
        toast.error(`Failed to link invoice: ${linkErr.message}`)
      }
      return
    }
    if (!linkedRows || linkedRows.length === 0) {
      // Job was already invoiced between load and click — void the orphan, don't double-bill.
      const { error: voidErr } = await supabase.from('invoices').update({ status: 'void' }).eq('id', invoice.id)
      queryClient.invalidateQueries({ queryKey: ['field-job', id] })
      if (voidErr) {
        toast.warning(`This visit was already invoiced. A draft invoice (${invoice.invoice_number ?? invoice.id}) was created but could not be auto-voided — please void it manually.`)
      } else {
        toast.warning('This visit was already invoiced — no second invoice was created.')
      }
      return
    }
    // Slice 4: freeze the job's cost basis onto the new invoice (staff-only,
    // non-fatal — never throws, so a snapshot failure can't break the convert).
    await snapshotInvoiceCost(supabase, workspace.id, invoice.id, [job.id])
    queryClient.invalidateQueries({ queryKey: ['field-job', id] })
    queryClient.invalidateQueries({ queryKey: ['field-jobs-cal'] })
    toast.success('Invoice created')
    navigate(`/app/invoices/${invoice.id}`)
  }

  // Editor callbacks. Persist tax / discount / totals to field_jobs as soon as
  // they change in the editor; the parent owns these writes so the editor
  // stays a pure controlled component.
  const handleTaxRateChange = useCallback((rate: number) => {
    updateMutation.mutate({ tax_rate: rate })
  }, [updateMutation])

  const handleDiscountChange = useCallback((type: string, value: number) => {
    updateMutation.mutate({ discount_type: type, discount_value: value })
  }, [updateMutation])

  const handleTotalsChange = useCallback((patch: { subtotal: number; tax_amount: number; discount_amount: number; total: number }) => {
    updateMutation.mutate(patch)
  }, [updateMutation])

  if (isLoading) {
    return (
      <div style={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'var(--canvas)' }}>
        <div style={{ width: 32, height: 32, borderRadius: '50%', border: '2px solid var(--accent)', borderTopColor: 'transparent', animation: 'spin 0.8s linear infinite' }} />
      </div>
    )
  }

  if (!job) {
    return (
      <div style={{ height: '100%', background: 'var(--canvas)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', gap: 14, padding: 40 }}>
        <p style={{ fontSize: 15, fontWeight: 700, color: 'var(--ink-secondary)', margin: 0 }}>Job not found</p>
        <p style={{ fontSize: 13, color: 'var(--ink-tertiary)', margin: 0 }}>The job may have been deleted or belongs to a different workspace.</p>
        <button onClick={() => navigate('/app/field')}
          style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '7px 14px', background: 'var(--accent-dim)', border: '1px solid var(--accent-border)', borderRadius: 9, color: 'var(--accent-text)', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
          <ArrowLeft style={{ width: 13, height: 13 }} /> Back to Field Hub
        </button>
      </div>
    )
  }

  const cfg = JOB_STATUS_CONFIG[job.status] || JOB_STATUS_CONFIG.scheduled
  const nextStatus = NEXT_STATUS[job.status]
  const nextCfg = nextStatus ? JOB_STATUS_CONFIG[nextStatus] : null

  return (
    <div style={{ height: '100%', display: 'flex', flexDirection: 'column', background: 'var(--canvas)' }}>

      {/* Top bar — breadcrumb + title + action stack. Mirrors DealDetail pattern. */}
      <div style={{ padding: '14px 24px', borderBottom: '1px solid var(--border-subtle)', display: 'flex', alignItems: 'center', gap: 12, flexShrink: 0, background: 'var(--header-bg)' }}>
        <button onClick={() => { if (window.history.length > 1) navigate(-1); else navigate('/app/field') }}
          style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 13, color: 'var(--ink-tertiary)', background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}
          onMouseEnter={e => (e.currentTarget.style.color = 'var(--ink-secondary)')}
          onMouseLeave={e => (e.currentTarget.style.color = 'var(--ink-tertiary)')}>
          <ArrowLeft style={{ width: 14, height: 14 }} /> Back
        </button>
        <div style={{ width: 1, height: 16, background: 'var(--border-default)' }} />
        <span style={{ fontSize: 11, fontWeight: 700, color: 'var(--ink-tertiary)', fontFamily: 'monospace', flexShrink: 0 }}>{job.job_number}</span>
        <span style={{ fontSize: 13, color: 'var(--ink-primary)', fontWeight: 600, flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {job.title}
        </span>

        {/* Action stack — replicates JobDrawer's bottom action stack at the top here.
            Held jobs swap forward-advance for Resume; non-terminal jobs additionally
            expose a Hold… menu with the 4 hold choices. */}
        <div style={{ display: 'flex', gap: 8, flexShrink: 0, position: 'relative' }}>
          {HOLD_STATUSES.includes(job.status) ? (
            <button onClick={handleResume}
              style={{ display: 'flex', alignItems: 'center', gap: 5, padding: '6px 12px', background: 'var(--accent-dim)', border: '1px solid var(--accent-border)', borderRadius: 8, color: 'var(--accent-text)', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>
              <Play style={{ width: 12, height: 12 }} /> Resume → Scheduled
            </button>
          ) : (
            <>
              {job.status === 'scheduled' && (
                <button onClick={handleSendEnRoute}
                  style={{ display: 'flex', alignItems: 'center', gap: 5, padding: '6px 12px', background: 'var(--accent-dim)', border: '1px solid var(--accent-border)', borderRadius: 8, color: 'var(--accent-text)', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>
                  <Navigation style={{ width: 12, height: 12 }} /> Mark en route
                </button>
              )}
              {nextCfg && (
                <button onClick={handleStatusAdvance}
                  style={{ display: 'flex', alignItems: 'center', gap: 5, padding: '6px 12px', background: `color-mix(in srgb, ${nextCfg.color} 9%, transparent)`, border: `1px solid ${nextCfg.border}`, borderRadius: 8, color: nextCfg.color, fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>
                  → Mark {nextCfg.label}
                </button>
              )}
            </>
          )}
          {canEnterHold(job.status) && !HOLD_STATUSES.includes(job.status) && (
            <div style={{ position: 'relative' }}>
              <button onClick={() => setHoldMenuOpen(o => !o)}
                style={{ display: 'flex', alignItems: 'center', gap: 5, padding: '6px 12px', background: 'var(--field-bg)', border: '1px solid var(--border-default)', borderRadius: 8, color: 'var(--ink-secondary)', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>
                <Pause style={{ width: 12, height: 12 }} /> {holdMenuOpen ? 'Cancel' : 'Hold…'}
              </button>
              {holdMenuOpen && (
                <div style={{ position: 'absolute', top: 'calc(100% + 6px)', right: 0, display: 'flex', flexDirection: 'column', gap: 4, background: 'var(--surface-raised)', border: '1px solid var(--border-default)', borderRadius: 10, padding: 6, minWidth: 180, boxShadow: 'var(--shadow-popover-mid)', zIndex: 10 }}>
                  {HOLD_STATUSES.map(h => {
                    const hc = JOB_STATUS_CONFIG[h]
                    return (
                      <button key={h} onClick={() => handleEnterHold(h)}
                        style={{ padding: '7px 10px', background: hc.bg, border: `1px solid ${hc.border}`, borderRadius: 7, color: hc.color, fontSize: 12, fontWeight: 600, cursor: 'pointer', textAlign: 'left' }}>
                        {hc.label}
                      </button>
                    )
                  })}
                </div>
              )}
            </div>
          )}
          {job.status === 'complete' && !job.invoice_id && (
            <button onClick={handleGenerateInvoice} disabled={generatingInvoice}
              data-testid="job-detail-generate-invoice"
              style={{ display: 'flex', alignItems: 'center', gap: 5, padding: '6px 12px', background: 'var(--positive-dim)', border: '1px solid var(--positive-border)', borderRadius: 8, color: 'var(--positive)', fontSize: 12, fontWeight: 600, cursor: generatingInvoice ? 'wait' : 'pointer', opacity: generatingInvoice ? 0.6 : 1 }}>
              <FileText style={{ width: 12, height: 12 }} /> {generatingInvoice ? 'Creating…' : 'Generate invoice'}
            </button>
          )}
          {job.invoice_id && (
            <button onClick={() => navigate(`/app/invoices/${job.invoice_id}`)}
              data-testid="job-detail-view-invoice"
              style={{ display: 'flex', alignItems: 'center', gap: 5, padding: '6px 12px', background: 'var(--field-bg)', border: '1px solid var(--border-default)', borderRadius: 8, color: 'var(--ink-primary)', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>
              <ExternalLink style={{ width: 12, height: 12 }} /> View invoice
            </button>
          )}
        </div>
      </div>

      {/* Body — scrollable two-column layout. Halt 3 shows the left column
          header card + section placeholders. Inline edit, line items CRUD,
          related records all land in subsequent halts. */}
      <div style={{ flex: 1, overflow: 'auto', padding: '24px 28px' }}>
        {/* Desktop: main column + 320px right rail. Mobile (<768px): single
            column — the rail stacks below the main content. */}
        <div style={{ maxWidth: 1100, margin: '0 auto', display: 'grid', gridTemplateColumns: isMobile ? 'minmax(0, 1fr)' : 'minmax(0, 1fr) 320px', gap: 20 }}>

          {/* Left column */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>

            {/* Vendor completion banner. Fires only when the latest vendor
                lifecycle event is `completed` AND field_jobs.status is not
                yet `complete`. The button routes through savePatch — the
                identical inline-edit path the status picker uses — so the
                notifyPortal(job_completed) side-effect lives in exactly
                one place. Dismissible per-session via local state. */}
            {latestVendorEvent?.event_type === 'completed'
              && job.status !== 'complete'
              && !completionBannerDismissed && (
              <VendorCompletionBanner
                vendorName={latestAssignment?.vendor?.name ?? null}
                completedAt={latestVendorEvent.created_at}
                isLocked={!!job.invoice_id}
                onMarkComplete={() => savePatch(buildStatusTransitionPayload(job.status, 'complete'))}
                onDismiss={() => setCompletionBannerDismissed(true)}
              />
            )}

            {/* Header card — inline-editable title + read-only badges. Detail
                fields (customer / schedule / address / assignment / notes)
                live in the collapsible cards below; this card is intentionally
                slim now. */}
            <div style={{ background: 'var(--hover-bg)', border: '1px solid var(--border-subtle)', borderRadius: 16, padding: '16px 18px' }}>
              <InlineField
                variant="text"
                value={job.title}
                onSave={async (next) => savePatch({ title: next })}
                required
                placeholder="Job title"
                isLocked={!!job.invoice_id}
                valueStyle={{ fontSize: 22, fontWeight: 900, color: 'var(--ink-primary)', letterSpacing: '-0.02em', lineHeight: 1.2 }}
              />
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginTop: 8, paddingLeft: 8 }}>
                <span style={{ fontSize: 11, fontWeight: 700, padding: '3px 9px', borderRadius: 100, background: cfg.bg, color: cfg.color, border: `1px solid ${cfg.border}` }}>{cfg.label}</span>
                {latestVendorEvent && (
                  <VendorLifecycleHeaderChip event={latestVendorEvent} />
                )}
                {job.service_line?.name && (
                  <span style={{ fontSize: 11, fontWeight: 600, padding: '3px 9px', borderRadius: 100, background: 'var(--accent-dim)', color: 'var(--accent-text)', border: '1px solid var(--accent-border)' }}>
                    {job.service_line.name}
                  </span>
                )}
                <JobPriorityChip priority={job.priority} size="sm" />
              </div>
            </div>

            {/* Recurring-series context banner. Visible only when this job
                is one occurrence of a materialized series. Hosts both
                per-occurrence (skip) and per-series (end) actions. Hidden
                gracefully if the series row is gone (deleted upstream). */}
            {!!job.series_id && series && (
              <div style={seriesBannerStyles.wrap}>
                <Repeat style={seriesBannerStyles.icon} />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <p style={seriesBannerStyles.title}>
                    Part of a recurring series — {humanizeRule(series)}
                    {series.status === 'ended' && <span style={seriesBannerStyles.endedTag}>Ended</span>}
                  </p>
                  <p style={seriesBannerStyles.subtitle}>
                    {series.title}
                    {series.start_date && ` · started ${format(parseISO(series.start_date), 'MMM d, yyyy')}`}
                    {series.status === 'ended' && series.end_date && ` · ended ${format(parseISO(series.end_date), 'MMM d, yyyy')}`}
                  </p>
                  {series.status === 'active' && (
                    <div style={seriesBannerStyles.actions}>
                      {job.status !== 'cancelled' && job.status !== 'complete' && (
                        <button type="button" onClick={handleSkipVisit} style={seriesBannerStyles.skipBtn}>
                          Skip this visit
                        </button>
                      )}
                      <button type="button" onClick={() => setEndModalOpen(true)} style={seriesBannerStyles.endBtn}>
                        End series
                      </button>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Lock banner — visible once the job has been invoiced. The
                "View invoice" action in the page header is the link out;
                we deliberately don't widen the field-job query just to
                surface the invoice_number here (per Halt 5 decision). */}
            {!!job.invoice_id && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px 14px', background: 'var(--warning-dim)', border: '1px solid var(--warning-border)', borderRadius: 10 }}>
                <CheckCircle2 style={{ width: 14, height: 14, color: 'var(--warning)', flexShrink: 0 }} />
                <span style={{ fontSize: 12, color: 'var(--ink-secondary)' }}>
                  <strong style={{ color: 'var(--warning)', fontWeight: 700 }}>Line items locked</strong> — this job has been invoiced. Use “View invoice” to edit billing.
                </span>
              </div>
            )}

            <JobLineItemsEditor
              fieldJobId={job.id}
              currency={job.currency ?? 'USD'}
              taxRate={Number(job.tax_rate) || 0}
              discountType={job.discount_type ?? 'none'}
              discountValue={Number(job.discount_value) || 0}
              onTaxRateChange={handleTaxRateChange}
              onDiscountChange={handleDiscountChange}
              onTotalsChange={handleTotalsChange}
              isLocked={!!job.invoice_id}
              vendorActual={jobVendorActual}
              hasVendorAssignments={!!latestAssignment}
              laborCost={jobLaborCost}
            />

            {/* Internal in-house labor cost (slice 3) — staff-only, append-only.
                Applies to ANY job (not just subbed), so it sits here next to the
                line items rather than inside the vendor-assignment card. */}
            <LaborSection fieldJobId={job.id} canManage={isOwnerOrAdmin} />

            {/* Customer card */}
            <Card title="Customer" defaultOpen>
              <FieldRow label="Contact">
                <InlineField
                  variant="select"
                  value={job.contact_id}
                  displayValue={job.contact_name || ''}
                  options={contactOptions}
                  onSave={async (option: InlineFieldOption | null) => {
                    if (!option) {
                      // contact_id is NOT NULL; required=true gates this, but
                      // belt-and-suspenders here too.
                      throw new Error('Contact is required')
                    }
                    await savePatch({
                      contact_id: option.value,
                      contact_name: option.label,
                      contact_phone: option.phone || '',
                    })
                  }}
                  required
                  isLocked={!!job.invoice_id}
                  placeholder="Select contact"
                />
              </FieldRow>
              <FieldRow label="Phone">
                <InlineField
                  variant="text"
                  value={job.contact_phone}
                  onSave={async (next) => savePatch({ contact_phone: next || null })}
                  isLocked={!!job.invoice_id}
                  placeholder="Add phone"
                />
              </FieldRow>
              <FieldRow label="Company">
                <InlineField
                  variant="text"
                  value={job.company_name}
                  onSave={async (next) => savePatch({ company_name: next || null })}
                  isLocked={!!job.invoice_id}
                  placeholder="Add company"
                />
              </FieldRow>
            </Card>

            {/* Schedule card */}
            <Card title="Schedule" defaultOpen>
              <FieldRow label="Date">
                <InlineField
                  variant="date"
                  value={job.scheduled_date}
                  onSave={async (next) => savePatch({ scheduled_date: next || null })}
                  isLocked={!!job.invoice_id}
                  placeholder="Add date"
                />
              </FieldRow>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                <FieldRow label="Start">
                  <InlineField
                    variant="time"
                    value={job.scheduled_start}
                    onSave={async (next) => savePatch({ scheduled_start: next || null })}
                    isLocked={!!job.invoice_id}
                    placeholder="—"
                  />
                </FieldRow>
                <FieldRow label="End">
                  <InlineField
                    variant="time"
                    value={job.scheduled_end}
                    onSave={async (next) => savePatch({ scheduled_end: next || null })}
                    isLocked={!!job.invoice_id}
                    placeholder="—"
                  />
                </FieldRow>
              </div>
              <FieldRow label="Priority">
                <InlineField
                  variant="select"
                  value={job.priority || 'normal'}
                  displayValue={(PRIORITY_OPTIONS.find(p => p.value === (job.priority || 'normal')) || PRIORITY_OPTIONS[1]).label}
                  options={PRIORITY_OPTIONS}
                  onSave={async (option: InlineFieldOption | null) => savePatch({ priority: option?.value || 'normal' })}
                  required
                  isLocked={!!job.invoice_id}
                />
              </FieldRow>
              {/* External-reference fields. Free-text per client convention;
                  no format validation. Searchable from the FieldHub calendar
                  so "PO 4471" read over the phone finds the job. */}
              <FieldRow label="Work order #">
                <InlineField
                  variant="text"
                  value={job.work_order_number}
                  onSave={async (next) => savePatch({ work_order_number: next || null })}
                  isLocked={!!job.invoice_id}
                  placeholder="Add WO#"
                />
              </FieldRow>
              <FieldRow label="PO #">
                <InlineField
                  variant="text"
                  value={job.purchase_order_number}
                  onSave={async (next) => savePatch({ purchase_order_number: next || null })}
                  isLocked={!!job.invoice_id}
                  placeholder="Add PO#"
                />
              </FieldRow>
            </Card>

            {/* Address card */}
            <Card title="Address" defaultOpen>
              <FieldRow label="Location (denormalized into address)">
                <InlineField
                  variant="select"
                  value={job.location_id}
                  displayValue={job.location?.name ? (job.location.company?.name ? `${job.location.company.name} – ${job.location.name}` : job.location.name) : ''}
                  options={locationOptions}
                  onSave={async (option: InlineFieldOption | null) => {
                    if (!option) {
                      await savePatch({ location_id: null })
                      return
                    }
                    await savePatch({
                      location_id: option.value,
                      address_line1: option.address_line1 || '',
                      address_line2: option.address_line2 || '',
                      address_city: option.city || '',
                      address_state: option.state || '',
                      address_zip: option.zip || '',
                      address_country: option.country || '',
                    })
                  }}
                  isLocked={!!job.invoice_id}
                  placeholder="Select location (optional)"
                />
              </FieldRow>
              <FieldRow label="Street">
                <InlineField
                  variant="text"
                  value={job.address_line1}
                  onSave={async (next) => savePatch({ address_line1: next || null })}
                  isLocked={!!job.invoice_id}
                  placeholder="Street address"
                />
              </FieldRow>
              <FieldRow label="Line 2">
                <InlineField
                  variant="text"
                  value={job.address_line2}
                  onSave={async (next) => savePatch({ address_line2: next || null })}
                  isLocked={!!job.invoice_id}
                  placeholder="Suite, apt, unit (optional)"
                />
              </FieldRow>
              <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr', gap: 12 }}>
                <FieldRow label="City">
                  <InlineField
                    variant="text"
                    value={job.address_city}
                    onSave={async (next) => savePatch({ address_city: next || null })}
                    isLocked={!!job.invoice_id}
                    placeholder="City"
                  />
                </FieldRow>
                <FieldRow label="State">
                  <InlineField
                    variant="text"
                    value={job.address_state}
                    onSave={async (next) => savePatch({ address_state: next || null })}
                    isLocked={!!job.invoice_id}
                    placeholder="State"
                  />
                </FieldRow>
                <FieldRow label="Zip">
                  <InlineField
                    variant="text"
                    value={job.address_zip}
                    onSave={async (next) => savePatch({ address_zip: next || null })}
                    isLocked={!!job.invoice_id}
                    placeholder="Zip"
                  />
                </FieldRow>
              </div>
              <FieldRow label="Country">
                <InlineField
                  variant="text"
                  value={job.address_country}
                  onSave={async (next) => savePatch({ address_country: next || null })}
                  isLocked={!!job.invoice_id}
                  placeholder="US"
                />
              </FieldRow>
            </Card>

            {/* Photos card — sits between Address and Assignment per the
                dispatch's "between Address and Notes" anchor. Grid layout
                in JobDetail (vs. the strip used in JobDrawer). Shared
                upload, signed-URL caching, and lightbox via the
                JobPhotoStrip module. */}
            {workspace?.id && (
              <Card title="Photos">
                <JobPhotoGrid jobId={job.id} workspaceId={workspace.id} />
              </Card>
            )}

            {/* Assignment card */}
            <Card title="Assignment">
              <FieldRow label="Technician">
                <InlineField
                  variant="select"
                  value={job.assigned_agent_id}
                  displayValue={job.assigned_agent_name || ''}
                  options={agentOptions}
                  onSave={async (option: InlineFieldOption | null) => savePatch({
                    assigned_agent_id: option?.value || null,
                    assigned_agent_name: option?.label || null,
                  })}
                  isLocked={!!job.invoice_id}
                  placeholder="Unassigned"
                />
              </FieldRow>
              <FieldRow label="CSR">
                {/* csr_id has no denormalized name column — resolve label
                    from the same agents list the technician picker uses. */}
                <InlineField
                  variant="select"
                  value={job.csr_id}
                  displayValue={agents.find((a: any) => a.id === job.csr_id)?.full_name || ''}
                  options={agentOptions}
                  onSave={async (option: InlineFieldOption | null) => savePatch({ csr_id: option?.value || null })}
                  isLocked={!!job.invoice_id}
                  placeholder="None"
                />
              </FieldRow>
              <FieldRow label="Vendor">
                {/* Inline send-to-vendor affordance: visible only in the
                    pre-first-send window (no vendor_assignments row yet)
                    and only for owners/admins, matching the gate the
                    card's Send button uses. job.vendor_id is intentionally
                    NOT required — openSendModal handles vendor selection
                    (defaults to the job's pointer if eligible, else the
                    first email-bearing vendor). Once any assignment has
                    been sent, the card returns and this button disappears. */}
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <InlineField
                      variant="select"
                      value={job.vendor_id}
                      displayValue={job.vendor?.name || ''}
                      options={vendorOptions}
                      onSave={async (option: InlineFieldOption | null) => savePatch({ vendor_id: option?.value || null })}
                      isLocked={!!job.invoice_id}
                      placeholder="None — internal"
                    />
                  </div>
                  {!latestAssignment && isOwnerOrAdmin && (
                    <button
                      type="button"
                      onClick={openSendModal}
                      style={{
                        display: 'inline-flex', alignItems: 'center', gap: 5,
                        padding: '5px 10px', borderRadius: 7,
                        background: 'var(--accent-dim)',
                        border: '1px solid var(--accent-border)',
                        color: 'var(--accent-text)',
                        fontSize: 11, fontWeight: 700, cursor: 'pointer',
                        flexShrink: 0, whiteSpace: 'nowrap',
                      }}
                    >
                      <Send style={{ width: 11, height: 11 }} />
                      Send to vendor
                    </button>
                  )}
                </div>
              </FieldRow>
              <FieldRow label="Service line">
                <InlineField
                  variant="select"
                  value={job.service_line_id}
                  displayValue={job.service_line?.name || ''}
                  options={serviceLineOptions}
                  onSave={async (option: InlineFieldOption | null) => savePatch({ service_line_id: option?.value || null })}
                  isLocked={!!job.invoice_id}
                  placeholder="None"
                />
              </FieldRow>
            </Card>

            {conflicts.length > 0 && !conflictsDismissed && (
              <div style={conflictBannerStyles.wrap}>
                <AlertTriangle style={conflictBannerStyles.icon} />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 10, marginBottom: 4 }}>
                    <p style={conflictBannerStyles.title}>
                      Schedule conflict — {conflicts.length} overlapping {conflicts.length === 1 ? 'job' : 'jobs'} at this time
                    </p>
                    <button type="button"
                      onClick={() => setConflictsDismissed(true)}
                      aria-label="Dismiss conflict warning"
                      style={conflictBannerStyles.dismiss}>
                      <X style={{ width: 13, height: 13 }} />
                    </button>
                  </div>
                  <p style={conflictBannerStyles.subtitle}>
                    The assignee is already booked. Reassign one of these jobs to clear.
                  </p>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                    {conflicts.map(c => (
                      <button key={c.id} type="button"
                        onClick={() => navigate(`/app/jobs/${c.id}`)}
                        style={conflictBannerStyles.row}>
                        <span style={conflictBannerStyles.jobNumber}>{c.job_number || '—'}</span>
                        <span style={conflictBannerStyles.jobTitle}>{c.title || 'Untitled job'}</span>
                        <span style={conflictBannerStyles.jobTime}>
                          {c.scheduled_start ? `${c.scheduled_start}${c.scheduled_end ? `–${c.scheduled_end}` : ''}` : 'all day'}
                        </span>
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {/* Vendor assignment card — drives the tokenized accept/decline
                flow (separate from the inline Vendor pointer field above,
                which is a plain pointer edit). Renders only AFTER a first
                assignment exists; the no-assignment empty state was replaced
                by the inline "Send to vendor" button on the Vendor FieldRow
                above. State-driven once shown: pending → Cancel button
                (force cancel-first); terminal → Send button (partial-unique
                allows re-send). Non-owner/admin sees status read-only. */}
            {latestAssignment && (
            <div style={vendorAssignStyles.cardWrap}>
              <button
                type="button"
                onClick={() => setVendorCardOpen(o => !o)}
                style={vendorAssignStyles.cardHeader}
                onMouseEnter={e => (e.currentTarget.style.background = 'var(--hover-bg)')}
                onMouseLeave={e => (e.currentTarget.style.background = 'none')}
              >
                <span style={vendorAssignStyles.cardLabelGroup}>
                  <span style={vendorAssignStyles.cardLabel}>Vendor assignment</span>
                  {latestAssignment.status === 'pending' && (
                    <span style={vendorAssignStyles.pendingTag}>Pending</span>
                  )}
                </span>
                <ChevronDown style={{ ...vendorAssignStyles.chevron, transform: vendorCardOpen ? 'rotate(0deg)' : 'rotate(-90deg)' }} />
              </button>
              {vendorCardOpen && (
                <div style={vendorAssignStyles.body}>
                  <VendorAssignmentBody
                    latestAssignment={latestAssignment}
                    isOwnerOrAdmin={isOwnerOrAdmin}
                    onSendClick={openSendModal}
                    onCancelClick={() => setCancelConfirmOpen(true)}
                    fieldJobId={id!}
                  />
                </div>
              )}
            </div>
            )}

            {/* Notes card */}
            <Card title="Notes">
              {/* "Job details" — primary intake description. Aligned with
                  the same field's label in NewJobModal. job_notes flows
                  to invoices.notes on the job→invoice conversion path. */}
              <FieldRow label="Job details">
                <InlineField
                  variant="textarea"
                  value={job.job_notes}
                  onSave={async (next) => savePatch({ job_notes: next || null })}
                  isLocked={!!job.invoice_id}
                  placeholder="Describe the work — also appears on the invoice"
                />
              </FieldRow>
              <FieldRow label="Internal notes (team only)">
                <InlineField
                  variant="textarea"
                  value={job.internal_notes}
                  onSave={async (next) => savePatch({ internal_notes: next || null })}
                  isLocked={!!job.invoice_id}
                  placeholder="Access codes, internal instructions, etc."
                />
              </FieldRow>
            </Card>
          </div>

          {/* Right column */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            {/* Halt 4 / 5 lands related records here */}
            <SectionPlaceholder title="Related" note="Halt 4/5 — contact link, deal link, invoice link" />
          </div>
        </div>
      </div>

      {/* End-series confirmation. Destructive — count-led headline, red CTA,
          Cancel autoFocused as the safe default. Closes on overlay click. */}
      {endModalOpen && (() => {
        const count = endPreview?.count ?? 0
        const first = endPreview?.first
        const last  = endPreview?.last
        const dateRangeText = first && last
          ? (first === last
              ? format(parseISO(first), 'MMM d, yyyy')
              : `${format(parseISO(first), 'MMM d, yyyy')} and ${format(parseISO(last), 'MMM d, yyyy')}`)
          : null
        return (
          <div style={endSeriesModalStyles.overlay} onClick={() => !ending && setEndModalOpen(false)}>
            <div style={endSeriesModalStyles.content} onClick={e => e.stopPropagation()}>
              <h3 style={endSeriesModalStyles.headline}>
                {count === 0
                  ? 'End this series? No upcoming visits to remove.'
                  : `Delete ${count} upcoming visit${count === 1 ? '' : 's'} and end this series?`}
              </h3>
              <p style={endSeriesModalStyles.body}>
                {count > 0 && dateRangeText && (
                  <>Visits between {dateRangeText} will be permanently removed. </>
                )}
                Completed and in-progress visits are kept. This can't be undone.
              </p>
              <div style={endSeriesModalStyles.actions}>
                <button type="button" autoFocus onClick={() => setEndModalOpen(false)} disabled={ending} style={endSeriesModalStyles.cancelBtn}>
                  Cancel
                </button>
                <button type="button" onClick={handleEndSeries} disabled={ending} style={endSeriesModalStyles.confirmBtn}>
                  {ending
                    ? 'Ending…'
                    : count > 0
                      ? `End series & delete ${count} visit${count === 1 ? '' : 's'}`
                      : 'End series'}
                </button>
              </div>
            </div>
          </div>
        )
      })()}

      {/* Send-assignment modal. Vendor picker + optional budget. Disabled
          rows for emailless vendors keep the "fix the vendor first" path
          visible instead of silently excluding them. */}
      {sendModalOpen && (
        <div style={vendorSendModalStyles.overlay} onClick={() => !sending && setSendModalOpen(false)}>
          <div style={vendorSendModalStyles.content} onClick={e => e.stopPropagation()}>
            <h3 style={vendorSendModalStyles.headline}>Send vendor assignment</h3>
            <p style={vendorSendModalStyles.helper}>
              The vendor will receive an email with Accept and Decline links. Reminders fire at the workspace's configured intervals.
            </p>
            <div style={vendorSendModalStyles.fieldGroup}>
              <div>
                <label style={vendorSendModalStyles.label}>Vendor</label>
                <select
                  value={sendVendorId}
                  onChange={e => setSendVendorId(e.target.value)}
                  disabled={sending}
                  style={vendorSendModalStyles.select}
                >
                  {assignablePickerVendors.length === 0 && (
                    <option value="" disabled>No active vendors</option>
                  )}
                  {assignablePickerVendors.map((v: any) => (
                    <option key={v.id} value={v.id} disabled={!v.email}>
                      {v.name}{v.email ? '' : '  (no email)'}
                    </option>
                  ))}
                </select>
                {assignablePickerVendors.some((v: any) => !v.email) && (
                  <p style={vendorSendModalStyles.fieldHint}>
                    Vendors without an email can't be assigned — add one on the vendor's profile first.
                  </p>
                )}
              </div>
              <div>
                <label style={vendorSendModalStyles.label}>Budget offered (optional)</label>
                <input
                  type="number"
                  min={0}
                  step={1}
                  value={sendBudget}
                  onChange={e => setSendBudget(e.target.value)}
                  placeholder="—"
                  disabled={sending}
                  style={vendorSendModalStyles.input}
                />
                <p style={vendorSendModalStyles.fieldHint}>
                  Vendor-facing price. Leave blank to omit from the email.
                </p>
              </div>
              {sendVendorId && <VendorComplianceWarning vendorId={sendVendorId} />}
              {sendVendorId && job?.scheduled_date && (
                <VendorBusyAdvisory
                  vendorId={sendVendorId}
                  jobDate={job.scheduled_date}
                  jobStart={job.scheduled_start}
                  jobEnd={job.scheduled_end}
                  timezone={workspace?.timezone ?? null}
                />
              )}
            </div>
            <div style={vendorSendModalStyles.actions}>
              <button type="button" onClick={() => setSendModalOpen(false)} disabled={sending} style={vendorSendModalStyles.cancelBtn}>
                Cancel
              </button>
              <button type="button" onClick={handleSendAssignment} disabled={sending || !sendVendorId} style={vendorSendModalStyles.sendBtn}>
                <Send style={{ width: 13, height: 13 }} />
                {sending ? 'Sending…' : 'Send assignment'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Cancel-pending-assignment confirmation. Destructive — the vendor's
          live accept/decline links will stop working at the next request. */}
      {cancelConfirmOpen && (
        <div style={endSeriesModalStyles.overlay} onClick={() => !cancelling && setCancelConfirmOpen(false)}>
          <div style={endSeriesModalStyles.content} onClick={e => e.stopPropagation()}>
            <h3 style={endSeriesModalStyles.headline}>Cancel this pending assignment?</h3>
            <p style={endSeriesModalStyles.body}>
              The vendor's accept/decline links will stop working and pending reminders won't fire. You can send a new assignment afterward.
            </p>
            <div style={endSeriesModalStyles.actions}>
              <button type="button" autoFocus onClick={() => setCancelConfirmOpen(false)} disabled={cancelling} style={endSeriesModalStyles.cancelBtn}>
                Keep assignment
              </button>
              <button type="button" onClick={handleCancelAssignment} disabled={cancelling} style={endSeriesModalStyles.confirmBtn}>
                {cancelling ? 'Cancelling…' : 'Cancel assignment'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

// Vendor-assignment card body — pure render of the current state. Branches
// on latestAssignment.status: pending → status line + Cancel; terminal
// (accepted/declined/cancelled/expired) → status line + Send (prior is
// terminal, partial-unique allows re-send).
// Non-owner/admin sees status only.
//
// The null/no-assignment branch is INTENTIONALLY absent — the parent
// hides the entire card in that case and renders an inline "Send to
// vendor" button next to the Vendor FieldRow instead. This component
// runs only when latestAssignment is a real row.
function VendorAssignmentBody({
  latestAssignment, isOwnerOrAdmin, onSendClick, onCancelClick, fieldJobId,
}: {
  latestAssignment: {
    id: string
    status: 'pending' | 'accepted' | 'declined' | 'expired' | 'cancelled'
    budget_offered: number | string | null
    responded_at: string | null
    decline_reason: string | null
    created_at: string
    vendor_id: string
    vendor: { name: string } | null
  }
  isOwnerOrAdmin: boolean
  onSendClick: () => void
  onCancelClick: () => void
  fieldJobId: string
}) {
  const s = latestAssignment
  // PostgREST returns numeric as either string or number depending on path;
  // gate the budget display on a positive numeric coercion either way.
  const budgetNum = s.budget_offered == null ? 0 : Number(s.budget_offered)
  const showBudget = !Number.isNaN(budgetNum) && budgetNum > 0
  const budgetText = showBudget ? `$${budgetNum.toLocaleString('en-US')}` : ''
  const vendorName = s.vendor?.name || 'vendor'

  const renderStatus = () => {
    if (s.status === 'pending') {
      return (
        <div style={vendorAssignStyles.statusRow}>
          <Clock style={{ ...vendorAssignStyles.statusIcon, ...vendorAssignStyles.iconPending }} />
          <div style={{ flex: 1, minWidth: 0 }}>
            <p style={vendorAssignStyles.statusText}>Pending — sent to {vendorName}</p>
            <p style={vendorAssignStyles.statusMeta}>
              {showBudget && <>{budgetText} · </>}Awaiting response · sent {format(parseISO(s.created_at), 'MMM d, h:mm a')}
            </p>
          </div>
        </div>
      )
    }
    if (s.status === 'accepted') {
      return (
        <div style={vendorAssignStyles.statusRow}>
          <CheckCircle2 style={{ ...vendorAssignStyles.statusIcon, ...vendorAssignStyles.iconAccepted }} />
          <div style={{ flex: 1, minWidth: 0 }}>
            <p style={vendorAssignStyles.statusText}>Accepted by {vendorName}</p>
            <p style={vendorAssignStyles.statusMeta}>
              {showBudget && <>{budgetText} · </>}{s.responded_at ? `Accepted ${format(parseISO(s.responded_at), 'MMM d, h:mm a')}` : 'Accepted'}
            </p>
          </div>
        </div>
      )
    }
    if (s.status === 'declined') {
      return (
        <div style={vendorAssignStyles.statusRow}>
          <XCircle style={{ ...vendorAssignStyles.statusIcon, ...vendorAssignStyles.iconDeclined }} />
          <div style={{ flex: 1, minWidth: 0 }}>
            <p style={vendorAssignStyles.statusText}>Declined by {vendorName}</p>
            <p style={vendorAssignStyles.statusMeta}>
              {showBudget && <>{budgetText} · </>}{s.responded_at ? `Declined ${format(parseISO(s.responded_at), 'MMM d, h:mm a')}` : 'Declined'}
            </p>
            {s.decline_reason && (
              <p style={vendorAssignStyles.declineReason}>“{s.decline_reason}”</p>
            )}
          </div>
        </div>
      )
    }
    if (s.status === 'expired') {
      return (
        <div style={vendorAssignStyles.statusRow}>
          <Clock style={{ ...vendorAssignStyles.statusIcon, ...vendorAssignStyles.iconCancelled }} />
          <div style={{ flex: 1, minWidth: 0 }}>
            <p style={vendorAssignStyles.statusText}>Expired — {vendorName} didn't respond in time</p>
            <p style={vendorAssignStyles.statusMeta}>
              {showBudget && <>{budgetText} · </>}Sent {format(parseISO(s.created_at), 'MMM d, h:mm a')}
            </p>
          </div>
        </div>
      )
    }
    // cancelled
    return (
      <div style={vendorAssignStyles.statusRow}>
        <Ban style={{ ...vendorAssignStyles.statusIcon, ...vendorAssignStyles.iconCancelled }} />
        <div style={{ flex: 1, minWidth: 0 }}>
          <p style={vendorAssignStyles.statusText}>Cancelled — {vendorName}</p>
          <p style={vendorAssignStyles.statusMeta}>
            {showBudget && <>{budgetText} · </>}Sent {format(parseISO(s.created_at), 'MMM d, h:mm a')}
          </p>
        </div>
      </div>
    )
  }

  return (
    <>
      {renderStatus()}
      {/* Vendor lifecycle timeline — only shown for accepted assignments.
          Read-only on the staff side; the vendor drives it from
          /vendor/jobs/:assignmentId. Latest-state chip + compact
          timeline below; null-renders when no events exist. */}
      {s.status === 'accepted' && <VendorLifecycleTimeline assignmentId={s.id} />}
      {!isOwnerOrAdmin ? (
        <p style={vendorAssignStyles.hint}>
          <AlertTriangle style={vendorAssignStyles.hintIcon} />
          Only owners and admins can manage assignments.
        </p>
      ) : s.status === 'pending' ? (
        <div style={vendorAssignStyles.actions}>
          <button type="button" onClick={onCancelClick} style={vendorAssignStyles.cancelBtn}>
            <Ban style={{ width: 12, height: 12 }} />
            Cancel assignment
          </button>
        </div>
      ) : (
        <div style={vendorAssignStyles.actions}>
          <button type="button" onClick={onSendClick} style={vendorAssignStyles.sendBtn}>
            <Send style={{ width: 12, height: 12 }} />
            Send assignment
          </button>
        </div>
      )}
      {/* Internal vendor-cost ledger (slice 2) — staff-only, append-only.
          Sums vendor_bills → vendor-actual; variance vs the NTE budget. */}
      <VendorBillsSection
        assignmentId={s.id}
        fieldJobId={fieldJobId}
        budgetOffered={budgetNum > 0 ? budgetNum : null}
        canManage={isOwnerOrAdmin}
      />
    </>
  )
}

// Internal vendor-cost ledger card section (slice 2 CP2). Staff-only — reads/
// writes vendor_bills, which has NO vendor/portal RLS policy (a vendor session
// gets 0 rows), so cost never reaches a vendor/customer surface. Append-only:
// add + delete (to correct), NO inline edit (there is no UPDATE policy — an
// edit UI would 403). vendor-actual = SUM(amount); zero bills = "no bill yet"
// (the unknown state), never $0. Variance = budget_offered − vendor-actual,
// shown only when a budget is set AND ≥1 bill exists.
function VendorBillsSection({ assignmentId, fieldJobId, budgetOffered, canManage }: {
  assignmentId: string
  fieldJobId: string
  budgetOffered: number | null
  canManage: boolean
}) {
  const { workspace, profile } = useAuth()
  const queryClient = useQueryClient()
  const [amount, setAmount] = useState('')
  const [notes, setNotes] = useState('')

  const { data: bills = [] } = useQuery({
    queryKey: ['vendor-bills', assignmentId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('vendor_bills')
        .select('id, amount, notes, created_at')
        .eq('vendor_assignment_id', assignmentId)
        .order('created_at', { ascending: true })
      if (error) throw error
      return (data ?? []) as Array<{ id: string; amount: number | string; notes: string | null; created_at: string }>
    },
    enabled: !!assignmentId,
  })

  const hasBills = bills.length > 0
  const vendorActual = bills.reduce((sum, b) => sum + (Number(b.amount) || 0), 0)
  // Variance only when a budget exists AND a bill has landed — never a fake
  // variance off zero bills.
  const variance = (budgetOffered != null && budgetOffered > 0 && hasBills) ? budgetOffered - vendorActual : null

  const money = (n: number) => `$${n.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`

  const addBill = useMutation({
    mutationFn: async () => {
      const amt = parseFloat(amount)
      if (!Number.isFinite(amt) || amt <= 0) throw new Error('Enter a bill amount greater than 0')
      const { error } = await supabase.from('vendor_bills').insert({
        workspace_id: workspace!.id,
        vendor_assignment_id: assignmentId,
        field_job_id: fieldJobId,
        amount: amt,
        notes: notes.trim() || null,
        recorded_by: profile?.id ?? null,
      })
      if (error) throw error
    },
    onSuccess: () => {
      setAmount(''); setNotes('')
      queryClient.invalidateQueries({ queryKey: ['vendor-bills', assignmentId] })
      queryClient.invalidateQueries({ queryKey: ['job-vendor-actual', fieldJobId] }) // refresh margin fold-in
    },
    onError: (e: any) => toast.error(e?.message || 'Failed to add bill'),
  })

  const deleteBill = useMutation({
    mutationFn: async (billId: string) => {
      const { error } = await supabase.from('vendor_bills').delete().eq('id', billId)
      if (error) throw error
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['vendor-bills', assignmentId] })
      queryClient.invalidateQueries({ queryKey: ['job-vendor-actual', fieldJobId] }) // refresh margin fold-in
    },
    onError: (e: any) => toast.error(e?.message || 'Failed to delete bill'),
  })

  const accent = 'var(--accent-text)'
  return (
    <div style={{ marginTop: 14, paddingTop: 12, borderTop: '1px solid var(--border-subtle)' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
        <span title="Internal vendor cost — never shown to the vendor or customer"
          style={{ fontSize: 10, fontWeight: 700, color: accent, textTransform: 'uppercase', letterSpacing: '0.08em', fontStyle: 'italic' }}>
          Vendor cost — internal
        </span>
        <span style={{ fontSize: 13, fontWeight: 800, color: 'var(--ink-primary)', fontVariantNumeric: 'tabular-nums' }}>
          {hasBills ? money(vendorActual) : <span style={{ fontSize: 11, fontWeight: 600, color: 'var(--ink-tertiary)', fontStyle: 'italic' }}>No bill received yet</span>}
        </span>
      </div>

      {/* Variance vs NTE budget — only with both a budget and ≥1 bill */}
      {variance != null && (
        <div title="budget_offered − vendor-actual. Internal." style={{ marginBottom: 8, fontSize: 11, fontWeight: 700, fontStyle: 'italic', fontVariantNumeric: 'tabular-nums', color: variance >= 0 ? 'var(--positive)' : 'var(--danger)' }}>
          {variance >= 0 ? `${money(variance)} under NTE` : `${money(-variance)} over NTE`}
        </div>
      )}

      {/* Bills list — amount · notes · date, with delete-to-correct (append-only,
          no inline edit since there's no UPDATE policy). */}
      {hasBills && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4, marginBottom: canManage ? 10 : 0 }}>
          {bills.map(b => (
            <div key={b.id} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, color: 'var(--ink-secondary)' }}>
              <span style={{ fontWeight: 700, color: 'var(--ink-primary)', fontVariantNumeric: 'tabular-nums', minWidth: 64 }}>{money(Number(b.amount) || 0)}</span>
              <span style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{b.notes || <span style={{ color: 'var(--ink-tertiary)' }}>—</span>}</span>
              <span style={{ fontSize: 10, color: 'var(--ink-tertiary)' }}>{format(parseISO(b.created_at), 'MMM d')}</span>
              {canManage && (
                <button type="button" title="Delete bill (corrections are delete + re-add)" onClick={() => deleteBill.mutate(b.id)}
                  style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--ink-tertiary)', padding: 2, display: 'flex' }}>
                  <X style={{ width: 13, height: 13 }} />
                </button>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Add-bill (owner/admin only). amount required > 0 — the unknown state is
          ZERO ROWS, not a null amount. */}
      {canManage && (
        <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
          <input type="number" value={amount} onChange={e => setAmount(e.target.value)} placeholder="Amount"
            style={{ width: 90, padding: '6px 8px', background: 'var(--field-bg)', border: '1px dashed var(--border-default)', borderRadius: 7, fontSize: 12, color: 'var(--ink-primary)', outline: 'none', fontVariantNumeric: 'tabular-nums' }} />
          <input value={notes} onChange={e => setNotes(e.target.value)} placeholder="Notes (optional)"
            style={{ flex: 1, minWidth: 0, padding: '6px 8px', background: 'var(--field-bg)', border: '1px solid var(--border-default)', borderRadius: 7, fontSize: 12, color: 'var(--ink-primary)', outline: 'none' }} />
          <button type="button" disabled={addBill.isPending || !amount.trim()} onClick={() => addBill.mutate()}
            style={{ display: 'flex', alignItems: 'center', gap: 4, padding: '6px 10px', background: 'var(--accent-dim)', border: '1px solid var(--accent-border)', borderRadius: 7, fontSize: 12, fontWeight: 700, color: accent, cursor: addBill.isPending || !amount.trim() ? 'default' : 'pointer', opacity: addBill.isPending || !amount.trim() ? 0.5 : 1, whiteSpace: 'nowrap' }}>
            <Plus style={{ width: 12, height: 12 }} /> Add bill
          </button>
        </div>
      )}
    </div>
  )
}

// Internal in-house labor-cost section (slice 3 CP2). Staff-only — reads/writes
// field_job_labor, which has NO vendor/portal RLS policy (a vendor session gets 0
// rows), so labor cost never reaches a customer/vendor surface. Append-only: add
// + delete (to correct), NO inline edit (there's no UPDATE policy — an edit UI
// would 403). amount is ALWAYS the source of truth; hours×rate is an OPTIONAL
// breakdown that auto-computes amount. The flat/volume/size path (type an amount
// + optional description, NO hours) is first-class — forcing hours would corrupt
// non-hourly work into fiction. Partial breakdown (only one of hours/rate) is
// ignored (dropped to null), never blocked. Zero entries = "no labor recorded"
// (the N/A state), never $0.
function LaborSection({ fieldJobId, canManage }: { fieldJobId: string; canManage: boolean }) {
  const { workspace, profile } = useAuth()
  const queryClient = useQueryClient()
  const [amount, setAmount] = useState('')
  const [hours, setHours] = useState('')
  const [rate, setRate] = useState('')
  const [description, setDescription] = useState('')

  const { data: entries = [] } = useQuery({
    queryKey: ['field-job-labor', fieldJobId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('field_job_labor')
        .select('id, amount, hours, rate, description, created_at')
        .eq('field_job_id', fieldJobId)
        .order('created_at', { ascending: true })
      if (error) throw error
      return (data ?? []) as Array<{ id: string; amount: number | string; hours: number | string | null; rate: number | string | null; description: string | null; created_at: string }>
    },
    enabled: !!fieldJobId,
  })

  const hasEntries = entries.length > 0
  const laborCost = entries.reduce((sum, e) => sum + (Number(e.amount) || 0), 0)
  const money = (n: number) => `$${n.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`

  // Dual path: BOTH hours & rate valid > 0 → amount is computed (hours×rate) and
  // the amount field is locked. Otherwise amount is directly editable (flat).
  const h = parseFloat(hours), r = parseFloat(rate)
  const bothBreakdown = hours.trim() !== '' && rate.trim() !== '' && Number.isFinite(h) && Number.isFinite(r) && h > 0 && r > 0
  const computedAmount = bothBreakdown ? h * r : null
  const effectiveAmount = computedAmount != null ? computedAmount : parseFloat(amount)
  const canSave = Number.isFinite(effectiveAmount) && effectiveAmount > 0

  const addEntry = useMutation({
    mutationFn: async () => {
      if (!canSave) throw new Error('Enter a labor amount greater than 0 (or hours × rate)')
      const { error } = await supabase.from('field_job_labor').insert({
        workspace_id: workspace!.id,
        field_job_id: fieldJobId,
        amount: effectiveAmount,
        hours: bothBreakdown ? h : null, // lone hours-or-rate is dropped, never stored as fiction
        rate: bothBreakdown ? r : null,
        description: description.trim() || null,
        recorded_by: profile?.id ?? null,
      })
      if (error) throw error
    },
    onSuccess: () => {
      setAmount(''); setHours(''); setRate(''); setDescription('')
      queryClient.invalidateQueries({ queryKey: ['field-job-labor', fieldJobId] })
      queryClient.invalidateQueries({ queryKey: ['job-labor-cost', fieldJobId] }) // refresh margin fold-in
    },
    onError: (e: any) => toast.error(e?.message || 'Failed to add labor'),
  })

  const deleteEntry = useMutation({
    mutationFn: async (entryId: string) => {
      const { error } = await supabase.from('field_job_labor').delete().eq('id', entryId)
      if (error) throw error
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['field-job-labor', fieldJobId] })
      queryClient.invalidateQueries({ queryKey: ['job-labor-cost', fieldJobId] }) // refresh margin fold-in
    },
    onError: (e: any) => toast.error(e?.message || 'Failed to delete labor'),
  })

  const accent = 'var(--accent-text)'
  const inputBase: React.CSSProperties = { padding: '6px 8px', background: 'var(--field-bg)', border: '1px solid var(--border-default)', borderRadius: 7, fontSize: 12, color: 'var(--ink-primary)', outline: 'none' }
  return (
    <div style={{ marginTop: 16, padding: '14px 16px', background: 'var(--surface)', border: '1px solid var(--border-default)', borderRadius: 14 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: (hasEntries || canManage) ? 10 : 0 }}>
        <span title="Internal labor cost — never shown to the customer or vendor"
          style={{ fontSize: 10, fontWeight: 700, color: accent, textTransform: 'uppercase', letterSpacing: '0.08em', fontStyle: 'italic' }}>
          Labor cost — internal
        </span>
        <span style={{ fontSize: 13, fontWeight: 800, color: 'var(--ink-primary)', fontVariantNumeric: 'tabular-nums' }}>
          {hasEntries ? money(laborCost) : <span style={{ fontSize: 11, fontWeight: 600, color: 'var(--ink-tertiary)', fontStyle: 'italic' }}>No labor recorded</span>}
        </span>
      </div>

      {/* Entries list — amount · breakdown/description · date, with delete-to-correct
          (append-only, no inline edit since there's no UPDATE policy). */}
      {hasEntries && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4, marginBottom: canManage ? 10 : 0 }}>
          {entries.map(e => {
            const eh = e.hours == null ? null : Number(e.hours)
            const er = e.rate == null ? null : Number(e.rate)
            const breakdown = (eh != null && er != null) ? `${eh}h × ${money(er)}` : null
            return (
              <div key={e.id} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, color: 'var(--ink-secondary)' }}>
                <span style={{ fontWeight: 700, color: 'var(--ink-primary)', fontVariantNumeric: 'tabular-nums', minWidth: 64 }}>{money(Number(e.amount) || 0)}</span>
                <span style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {breakdown && <span style={{ color: 'var(--ink-tertiary)' }}>{breakdown}{e.description ? ' · ' : ''}</span>}
                  {e.description || (!breakdown && <span style={{ color: 'var(--ink-tertiary)' }}>—</span>)}
                </span>
                <span style={{ fontSize: 10, color: 'var(--ink-tertiary)' }}>{format(parseISO(e.created_at), 'MMM d')}</span>
                {canManage && (
                  <button type="button" title="Delete labor entry (corrections are delete + re-add)" onClick={() => deleteEntry.mutate(e.id)}
                    style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--ink-tertiary)', padding: 2, display: 'flex' }}>
                    <X style={{ width: 13, height: 13 }} />
                  </button>
                )}
              </div>
            )
          })}
        </div>
      )}

      {/* Add entry — dual path. hours × rate auto-computes (and locks) amount; OR
          type an amount directly for flat/volume/size (NO hours required). */}
      {canManage && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
            <input type="number" value={hours} onChange={e => setHours(e.target.value)} placeholder="Hours" style={{ ...inputBase, width: 64, fontVariantNumeric: 'tabular-nums' }} />
            <span style={{ color: 'var(--ink-tertiary)', fontSize: 12 }}>×</span>
            <input type="number" value={rate} onChange={e => setRate(e.target.value)} placeholder="Rate" style={{ ...inputBase, width: 70, fontVariantNumeric: 'tabular-nums' }} />
            <span style={{ color: 'var(--ink-tertiary)', fontSize: 12 }}>=</span>
            <input type="number" value={computedAmount != null ? String(computedAmount) : amount} onChange={e => setAmount(e.target.value)} disabled={computedAmount != null}
              placeholder="Amount" title={computedAmount != null ? 'Computed from hours × rate' : 'Flat amount (or fill hours × rate)'}
              style={{ ...inputBase, width: 90, border: '1px dashed var(--border-default)', fontVariantNumeric: 'tabular-nums', opacity: computedAmount != null ? 0.7 : 1 }} />
          </div>
          <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
            <input value={description} onChange={e => setDescription(e.target.value)} placeholder="Description (optional — e.g. flat install, 3 rooms)" style={{ ...inputBase, flex: 1, minWidth: 0 }} />
            <button type="button" disabled={addEntry.isPending || !canSave} onClick={() => addEntry.mutate()}
              style={{ display: 'flex', alignItems: 'center', gap: 4, padding: '6px 10px', background: 'var(--accent-dim)', border: '1px solid var(--accent-border)', borderRadius: 7, fontSize: 12, fontWeight: 700, color: accent, cursor: (addEntry.isPending || !canSave) ? 'default' : 'pointer', opacity: (addEntry.isPending || !canSave) ? 0.5 : 1, whiteSpace: 'nowrap' }}>
              <Plus style={{ width: 12, height: 12 }} /> Add labor
            </button>
          </div>
        </div>
      )}
    </div>
  )
}

// Header chip surfacing the vendor's latest lifecycle event next to the
// job's own status pill. Reuses the shared STAFF_EVENT_COPY tokens so
// the same vocabulary appears here and on FieldHub JobCards. Tooltip
// carries relative time + delay reason when present (the operator's
// quick-look at "why is this delayed?" without opening the assignment).
function VendorLifecycleHeaderChip({ event }: {
  event: { event_type: StaffLifecycleEvent; created_at: string; note: string | null }
}) {
  const c = STAFF_EVENT_COPY[event.event_type]
  const rel = (() => {
    try {
      const ms = Date.now() - new Date(event.created_at).getTime()
      if (ms < 60_000) return 'just now'
      if (ms < 3_600_000) return `${Math.floor(ms / 60_000)}m ago`
      if (ms < 86_400_000) return `${Math.floor(ms / 3_600_000)}h ago`
      return `${Math.floor(ms / 86_400_000)}d ago`
    } catch { return '' }
  })()
  const title = `Vendor: ${c.label}${rel ? ' · ' + rel : ''}${event.note ? ' · ' + event.note : ''}`
  return (
    <span title={title}
      style={{ fontSize: 11, fontWeight: 700, padding: '3px 9px', borderRadius: 100, background: c.bg, color: c.color, border: `1px solid ${c.border}`, display: 'inline-flex', alignItems: 'center', gap: 5 }}>
      Vendor: {c.label}
    </span>
  )
}

// One-click "vendor said done, flip the job" affordance. Invokes the
// SAME savePatch path the inline status picker uses (recon-verified) —
// status update + notifyPortal({event:'job_completed'}) side-effect run
// in the canonical place, not reimplemented here. isLocked mirrors the
// invoice-gate that the rest of JobDetail respects (a job with an
// invoice can't change status).
function VendorCompletionBanner({ vendorName, completedAt, isLocked, onMarkComplete, onDismiss }: {
  vendorName: string | null
  completedAt: string
  isLocked: boolean
  onMarkComplete: () => void | Promise<void>
  onDismiss: () => void
}) {
  const rel = (() => {
    try {
      const ms = Date.now() - new Date(completedAt).getTime()
      if (ms < 60_000) return 'just now'
      if (ms < 3_600_000) return `${Math.floor(ms / 60_000)}m ago`
      if (ms < 86_400_000) return `${Math.floor(ms / 3_600_000)}h ago`
      return `${Math.floor(ms / 86_400_000)}d ago`
    } catch { return '' }
  })()
  return (
    <div role="status" style={{ background: 'var(--positive-dim)', border: '1px solid var(--positive-border)', borderRadius: 12, padding: '12px 16px', display: 'flex', alignItems: 'center', gap: 12 }}>
      <CheckCircle2 style={{ width: 18, height: 18, color: 'var(--positive)', flexShrink: 0 }} />
      <div style={{ flex: 1, minWidth: 0 }}>
        <p style={{ margin: 0, fontSize: 13, fontWeight: 700, color: 'var(--ink-primary)' }}>
          {vendorName ?? 'The vendor'} marked this complete{rel ? ` · ${rel}` : ''}
        </p>
        <p style={{ margin: '2px 0 0', fontSize: 12, color: 'var(--ink-secondary)' }}>
          Flip the job's own status to match. The portal gets the same notification a manual completion sends.
        </p>
      </div>
      <button type="button" onClick={onDismiss} title="Dismiss for this session"
        style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: 'var(--ink-tertiary)', padding: 4 }}>
        <X style={{ width: 14, height: 14 }} />
      </button>
      <button type="button" disabled={isLocked} onClick={onMarkComplete}
        title={isLocked ? "Locked — this job already has an invoice" : 'Mark job complete'}
        style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '8px 14px', background: 'var(--positive)', border: 'none', borderRadius: 9, color: 'var(--ink-inverse)', fontSize: 13, fontWeight: 700, cursor: isLocked ? 'not-allowed' : 'pointer', opacity: isLocked ? 0.6 : 1 }}>
        <CheckCircle2 style={{ width: 13, height: 13 }} />
        Mark job complete
      </button>
    </div>
  )
}

// Send-modal compliance warning. CHEAP version of the assignment guard:
// queries the chosen vendor's required_docs + latest vendor_documents,
// derives the worst state, and shows a non-blocking warning when any
// required doc is missing/expired/rejected. Pending review is treated
// as a soft "in progress" — not a warning. Does NOT block the send.
function VendorComplianceWarning({ vendorId }: { vendorId: string }) {
  const { data: vendor } = useQuery({
    queryKey: ['vendor-compliance-warning-required', vendorId],
    queryFn: async () => {
      const { data } = await supabase.from('vendors').select('required_docs').eq('id', vendorId).maybeSingle()
      return data as { required_docs: { w9?: boolean; cgl?: boolean; commercial_auto?: boolean; workers_comp?: boolean } } | null
    },
  })
  const { data: docs = [] } = useQuery({
    queryKey: ['vendor-compliance-warning-docs', vendorId],
    queryFn: async () => {
      const { data } = await supabase
        .from('vendor_documents')
        .select('doc_type, status, expiration_date, created_at')
        .eq('vendor_id', vendorId)
        .order('created_at', { ascending: false })
      return (data ?? []) as Array<{ doc_type: 'w9' | 'cgl' | 'commercial_auto' | 'workers_comp'; status: string; expiration_date: string | null; created_at: string }>
    },
  })

  const required = vendor?.required_docs ?? {}
  const ALL_TYPES: Array<'w9' | 'cgl' | 'commercial_auto' | 'workers_comp'> = ['w9', 'cgl', 'commercial_auto', 'workers_comp']
  const SHORT: Record<string, string> = { w9: 'W-9', cgl: 'CGL', commercial_auto: 'Auto', workers_comp: 'WC' }

  const issues: string[] = []
  for (const t of ALL_TYPES) {
    if (!required[t]) continue
    const latest = docs.find(d => d.doc_type === t)
    if (!latest) { issues.push(`${SHORT[t]} missing`); continue }
    if (latest.status === 'rejected') { issues.push(`${SHORT[t]} rejected`); continue }
    if (latest.status === 'confirmed' && latest.expiration_date && new Date(latest.expiration_date + 'T00:00:00Z').getTime() < Date.now()) {
      issues.push(`${SHORT[t]} expired`)
    }
  }
  if (issues.length === 0) return null
  return (
    <div style={{ padding: '8px 12px', background: 'var(--warning-dim)', border: '1px solid var(--warning-border)', borderRadius: 8, display: 'flex', alignItems: 'flex-start', gap: 8 }}>
      <AlertTriangle style={{ width: 13, height: 13, color: 'var(--warning)', flexShrink: 0, marginTop: 1 }} />
      <p style={{ margin: 0, fontSize: 12, color: 'var(--warning)', fontWeight: 600 }}>
        Compliance gap: {issues.join(' · ')}. Sending anyway is allowed.
      </p>
    </div>
  )
}

// ─── VendorBusyAdvisory ─────────────────────────────────────────────────────
// Calls vendor_busy_ranges (SECURITY DEFINER fn, EXECUTE granted to
// authenticated; scope_check via profiles → workspace_id) and surfaces
// the vendor's busy intervals for the job's local day. Non-blocking —
// staff can always proceed with the send.
//
// Privacy contract surfaced at the UI: the RPC returns ONLY
// {busy_from, busy_until} timestamptzs. No job id, no title, no
// 'source' (assigned vs self_entered), no count. The component renders
// only time strings — never anything that would imply what the busy is.
// This is the Phase 1 privacy guarantee made visible.
//
// Window strategy (locked decision — overshoot, not client tz math):
// the RPC takes timestamptz bounds. Rather than compose a tz-aware
// window in JS (fragile), we send a ~48h UTC slab centered on the job's
// UTC date (jobDate 00:00Z − 12h … + 36h). The fn's internal window
// filter trims to actual overlap. Returns the right ranges; might
// return adjacent same-UTC-day work that's not actually on the local
// day — the local-day filter below drops it. Privacy unchanged
// (overshoot leaks no content; the return type only has time bounds).
//
// Display tz math: Intl.DateTimeFormat with the timeZone option (no
// new deps; date-fns-tz is NOT installed). 'en-CA' locale gives
// 'YYYY-MM-DD' shape for the day comparison. hour12:false gives
// 'HH:MM' shape for time display.
//
// Overlap classification only fires when jobStart/jobEnd are HH:MM
// shaped (string comparison is then meaningful). Other field_jobs
// text-time shapes ('9am', '9:00 AM') fall through to "non-overlapping"
// silently — cosmetic degradation, not a correctness issue.
function VendorBusyAdvisory({
  vendorId, jobDate, jobStart, jobEnd, timezone,
}: {
  vendorId: string
  jobDate: string | null
  jobStart: string | null
  jobEnd: string | null
  timezone: string | null
}) {
  const { data: ranges = [] } = useQuery({
    queryKey: ['vendor-busy', vendorId, jobDate],
    enabled: !!vendorId && !!jobDate,
    queryFn: async () => {
      const dayUtcMs = new Date(jobDate + 'T00:00:00Z').getTime()
      const windowStart = new Date(dayUtcMs - 12 * 3600 * 1000).toISOString()
      const windowEnd   = new Date(dayUtcMs + 36 * 3600 * 1000).toISOString()
      const { data, error } = await supabase.rpc('vendor_busy_ranges', {
        p_vendor_id: vendorId,
        p_window_start: windowStart,
        p_window_end: windowEnd,
      })
      if (error) return [] as Array<{ busy_from: string; busy_until: string }>
      return (data ?? []) as Array<{ busy_from: string; busy_until: string }>
    },
  })

  if (!jobDate) return null

  const tz = timezone || 'UTC'
  // Workspace-local formatters — built-in Intl, no deps.
  const timeFmt = new Intl.DateTimeFormat('en-US', {
    hour: '2-digit', minute: '2-digit', hour12: false, timeZone: tz,
  })
  const dayFmt = new Intl.DateTimeFormat('en-CA', {
    year: 'numeric', month: '2-digit', day: '2-digit', timeZone: tz,
  })

  // Filter to ranges whose START is on the job's local day. A range
  // spanning midnight then appears only on its start day — acceptable
  // for Phase 3 (vendors rarely set jobs across midnight, and the
  // dispatcher is making a day-level decision).
  const sameDay = ranges.flatMap(r => {
    try {
      const from = new Date(r.busy_from)
      const until = new Date(r.busy_until)
      if (dayFmt.format(from) !== jobDate) return []
      return [{ from, until }]
    } catch { return [] }
  })

  if (sameDay.length === 0) return null

  // Overlap detection — only meaningful when jobStart/jobEnd parse as
  // HH:MM. Other shapes fall through to non-overlap classification.
  const hhmm = /^([0-9]{2}):([0-9]{2})$/
  const overlapEligible = !!jobStart && !!jobEnd && hhmm.test(jobStart) && hhmm.test(jobEnd)

  const classified = sameDay.map(({ from, until }) => {
    const fromText = timeFmt.format(from)
    const untilText = timeFmt.format(until)
    let overlaps = false
    if (overlapEligible) {
      // Both ends compared as 'HH:MM' strings — lexical comparison is
      // chronological for fixed-width 24h format.
      overlaps = fromText < jobEnd! && untilText > jobStart!
    }
    return { fromText, untilText, overlaps }
  })

  const overlapping = classified.filter(c => c.overlaps)
  const others = classified.filter(c => !c.overlaps)

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
      {overlapping.length > 0 && (
        <div style={{ padding: '8px 12px', background: 'var(--warning-dim)', border: '1px solid var(--warning-border)', borderRadius: 8, display: 'flex', alignItems: 'flex-start', gap: 8 }}>
          <AlertTriangle style={{ width: 13, height: 13, color: 'var(--warning)', flexShrink: 0, marginTop: 1 }} />
          <p style={{ margin: 0, fontSize: 12, color: 'var(--warning)', fontWeight: 600 }}>
            Vendor busy during this slot: {overlapping.map(c => `${c.fromText}–${c.untilText}`).join(' · ')}. Sending anyway is allowed.
          </p>
        </div>
      )}
      {others.length > 0 && (
        <p style={{ margin: 0, fontSize: 11, color: 'var(--ink-tertiary)', fontWeight: 500 }}>
          {overlapping.length > 0 ? 'Also busy today: ' : 'Vendor has other work today: '}
          {others.map(c => `${c.fromText}–${c.untilText}`).join(' · ')}
        </p>
      )}
    </div>
  )
}

// Staff-side read-only view of the vendor lifecycle. Reads
// vendor_assignment_events through the staff_workspace_read RLS policy
// (workspace-scoped via the assignment chain). Visuals live in
// lib/vendorLifecycleCopy so FieldHub JobCards share the same vocab.
import { STAFF_EVENT_COPY, type StaffLifecycleEvent } from '@/lib/vendorLifecycleCopy'
interface StaffAssignmentEvent {
  id: string
  event_type: StaffLifecycleEvent
  note: string | null
  scheduled_return_date: string | null
  created_at: string
}

function VendorLifecycleTimeline({ assignmentId }: { assignmentId: string }) {
  const { data: events = [] } = useQuery<StaffAssignmentEvent[]>({
    queryKey: ['staff-vendor-assignment-events', assignmentId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('vendor_assignment_events')
        .select('id, event_type, note, scheduled_return_date, created_at')
        .eq('assignment_id', assignmentId)
        .order('created_at', { ascending: false })
      if (error) throw error
      return (data ?? []) as StaffAssignmentEvent[]
    },
  })
  if (events.length === 0) return null
  const latest = events[0]
  const latestCfg = STAFF_EVENT_COPY[latest.event_type]
  return (
    <div style={{ borderTop: '1px solid var(--border-subtle)', marginTop: 10, paddingTop: 10 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, marginBottom: 8 }}>
        <p style={{ fontSize: 10, fontWeight: 700, color: 'var(--ink-tertiary)', textTransform: 'uppercase', letterSpacing: '0.08em', margin: 0 }}>Vendor status</p>
        <span style={{ display: 'inline-flex', alignItems: 'center', padding: '2px 8px', background: latestCfg.bg, border: `1px solid ${latestCfg.border}`, color: latestCfg.color, borderRadius: 999, fontSize: 10, fontWeight: 700 }}>
          {latestCfg.label}
        </span>
      </div>
      <ol style={{ listStyle: 'none', margin: 0, padding: 0, display: 'flex', flexDirection: 'column', gap: 6 }}>
        {events.map(ev => {
          const c = STAFF_EVENT_COPY[ev.event_type]
          return (
            <li key={ev.id} style={{ display: 'flex', gap: 8, alignItems: 'flex-start' }}>
              <span style={{ flexShrink: 0, marginTop: 4, width: 8, height: 8, borderRadius: '50%', background: c.color }} />
              <div style={{ flex: 1, minWidth: 0 }}>
                <p style={{ margin: 0, fontSize: 12, color: 'var(--ink-primary)', fontWeight: 600 }}>{c.label}</p>
                {ev.scheduled_return_date && (
                  <p style={{ margin: '1px 0 0', fontSize: 11, color: 'var(--ink-secondary)' }}>Return on {ev.scheduled_return_date}</p>
                )}
                {ev.note && <p style={{ margin: '1px 0 0', fontSize: 11, color: 'var(--ink-secondary)', whiteSpace: 'pre-wrap' }}>{ev.note}</p>}
                <p style={{ margin: '1px 0 0', fontSize: 10, color: 'var(--ink-tertiary)' }}>{format(parseISO(ev.created_at), 'MMM d, h:mm a')}</p>
              </div>
            </li>
          )
        })}
      </ol>
    </div>
  )
}

// Visual stub for sections that subsequent halts will populate. Intentionally
// labeled so the page reads as "intentionally incomplete" rather than broken.
function SectionPlaceholder({ title, note }: { title: string; note: string }) {
  return (
    <div style={{ background: 'var(--hover-bg)', border: '1px dashed var(--border-default)', borderRadius: 14, padding: '14px 18px' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10 }}>
        <p style={{ fontSize: 11, fontWeight: 700, color: 'var(--ink-tertiary)', textTransform: 'uppercase', letterSpacing: '0.08em', margin: 0 }}>{title}</p>
        <span style={{ fontSize: 11, color: 'var(--ink-tertiary)' }}>{note}</span>
      </div>
    </div>
  )
}

// Collapsible card. Open state is local — no need to lift it; the page never
// reads it. Customer/Schedule/Address default open per spec; Assignment/Notes
// default collapsed.
function Card({ title, defaultOpen = false, children }: { title: string; defaultOpen?: boolean; children: React.ReactNode }) {
  const [open, setOpen] = useState(defaultOpen)
  return (
    <div style={{ background: 'var(--hover-bg)', border: '1px solid var(--border-subtle)', borderRadius: 14, overflow: 'hidden' }}>
      <button onClick={() => setOpen(o => !o)}
        style={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 18px', background: 'none', border: 'none', cursor: 'pointer', textAlign: 'left' }}
        onMouseEnter={e => (e.currentTarget.style.background = 'var(--hover-bg)')}
        onMouseLeave={e => (e.currentTarget.style.background = 'none')}>
        <span style={{ fontSize: 12, fontWeight: 700, color: 'var(--ink-secondary)', textTransform: 'uppercase', letterSpacing: '0.08em' }}>
          {title}
        </span>
        <ChevronDown style={{ width: 14, height: 14, color: 'var(--ink-tertiary)', transform: open ? 'rotate(0deg)' : 'rotate(-90deg)', transition: 'transform 150ms ease' }} />
      </button>
      {open && (
        <div style={{ padding: '4px 18px 16px', display: 'flex', flexDirection: 'column', gap: 10 }}>
          {children}
        </div>
      )}
    </div>
  )
}

// Field row layout: small uppercase label above, InlineField below.
function FieldRow({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <p style={{ fontSize: 10, fontWeight: 600, color: 'var(--ink-tertiary)', textTransform: 'uppercase', letterSpacing: '0.08em', margin: '0 0 4px 8px' }}>{label}</p>
      {children}
    </div>
  )
}
